/**
 * 应用入口与装配（M2-02/M2-03）。
 *
 * 启动顺序：
 * 1. 跨源隔离（§2.7）——可能触发一次刷新，必须最先。
 * 2. 装配应用层：AppStore + 动作注册表 + FileGateway + OPFS 工作区。
 * 3. 挂载外壳（菜单栏来自动作注册表，画布来自 CanvasView）。
 * 4. 后台探测 wasm 能力、检查崩溃恢复镜像。
 *
 * 全局快捷键在这里挂：匹配到动作就 preventDefault——哪怕动作当前被禁用，
 * 也不能把 Ctrl+S 漏给浏览器的「保存网页」。
 */
import { render } from 'solid-js/web';
import { createSignal, Show } from 'solid-js';
import './ui/theme/tokens.css';
import './app.css';
import { AppShell } from './ui/shell/AppShell';
import { PerfPanel } from './ui/panels/PerfPanel';
import { CanvasView } from './ui/canvas/CanvasView';
import { EmptyCanvasOverlay } from './ui/canvas/EmptyCanvasOverlay';
import { MenuBar } from './ui/menu/MenuBar';
import { buildMenuModel } from './ui/menu/menu-model';
import { AppStore, type RgbColor } from './app/store';
import type { SelectionMask } from './engine/selection/mask';
import { ActionRegistry, paletteEntriesFromActions, type ActionServices } from './app/actions/registry';
import {
  ShortcutOverrideStore,
  actionForKeyWithOverrides,
  withEffectiveShortcuts,
} from './app/actions/shortcut-overrides';
import { ShortcutEditorDialog } from './ui/dialogs/ShortcutEditorDialog';
import { HelpPanel } from './ui/dialogs/HelpPanel';
import { OnboardingTour, type TourStep } from './ui/onboarding/OnboardingTour';
import { openKvStore } from './io/kv-store';
import { CommandPalette } from './ui/command-palette/CommandPalette';
import { registerStandardActions } from './app/actions/standard-actions';
import { registerTransformActions } from './app/actions/transform-actions';
import { registerClipboardActions } from './app/actions/clipboard-actions';
import { ClipboardGateway, createBrowserClipboardDeps } from './io/clipboard';
import { importDroppedFile, isDroppableImage } from './app/drop-import';
import { bootRecovery, createNewDocument, registerFileActions } from './app/file/file-actions';
import { exportDocument, flattenToWhite } from './app/export';
import { compositeDocument } from './app/composite';
import { encodeImage } from './io/codecs';
import { buildIndexedImage } from './io/codecs/palette';
import { encodeIndexedPng } from './io/codecs/png-indexed';
import { ExportDialog, type ExportPreviewRecipe } from './ui/export/ExportDialog';
import { SeamCarveDialog } from './ui/dialogs/SeamCarveDialog';
import { NewDocumentDialog } from './ui/dialogs/NewDocumentDialog';
import { BatchDialog } from './ui/dialogs/BatchDialog';
import { NewGuideDialog } from './ui/dialogs/NewGuideDialog';
import { registerImageActions } from './app/actions/image-actions';
import {
  applyCurvesDestructive,
  applyExposureDestructive,
  applyHueSaturationDestructive,
  applyLevelsDestructive,
  type PixelColorTransform,
} from './app/color-adjust';
import { curvesLut, levelsLut, adjustHsl, type LevelsParams, type HslParams, type CurvePoint } from './engine/adjustments/lut';
import { basicToneLut, DEFAULT_BASIC_PARAMS } from './engine/adjustments/basic';
import { LevelsDialog } from './ui/dialogs/LevelsDialog';
import { CurvesDialog } from './ui/dialogs/CurvesDialog';
import { ExposureDialog } from './ui/dialogs/ExposureDialog';
import { HueSaturationDialog } from './ui/dialogs/HueSaturationDialog';
import { resizeCanvas, type CanvasAnchor } from './app/canvas-size';
import { resizeImage } from './app/image-size';
import { CanvasSizeDialog } from './ui/dialogs/CanvasSizeDialog';
import { ImageSizeDialog } from './ui/dialogs/ImageSizeDialog';
import { FillDialog, type FillRequest } from './ui/dialogs/FillDialog';
import { StrokeDialog, type StrokeRequest } from './ui/dialogs/StrokeDialog';
import {
  GradientEditorDialog,
  gradientPreviewBackgroundImage,
} from './ui/dialogs/GradientEditorDialog';
import gradientSwatchStyles from './ui/dialogs/GradientEditorDialog.module.css';
import type { GradientStopInput } from './app/gradient-fill';
import { strokeSelection } from './app/stroke-edit';
import { contentAwareFillDocument, fillSelectionWithColor } from './app/selection-edit';
import { seamCarveDocument } from './app/seam-carve';
import { registerSelectionActions } from './app/actions/selection-actions';
import {
  registerSelectionModifyActions,
  type SelectionModifyKind,
} from './app/actions/selection-modify-actions';
import { applySelectionModify } from './app/selection-modify';
import { SelectionModifyDialog } from './ui/dialogs/SelectionModifyDialog';
import { ColorRangeDialog, COLOR_RANGE_DEFAULT_FUZZINESS } from './ui/dialogs/ColorRangeDialog';
import { FilterDialog, type FilterKind } from './ui/dialogs/FilterDialog';
import { LiquifyDialog } from './ui/dialogs/LiquifyDialog';
import { LiquifyController } from './interaction/tools/liquify-tool';
import { PuppetWarpDialog, PROXY_LONG_EDGE } from './ui/dialogs/PuppetWarpDialog';
import { PuppetWarpController } from './interaction/tools/puppet-tool';
import { registerEditActions } from './app/actions/edit-actions';
import { registerAdjustmentActions } from './app/actions/adjustment-actions';
import { registerFilterActions, runFilterWithParams, FILTER_NAMES } from './app/actions/filter-actions';
import { registerEffectActions } from './app/actions/effect-actions';
import { LayerStyleDialog } from './ui/dialogs/LayerStyleDialog';
import { ContextMenu, type ContextMenuItem } from './ui/menu/ContextMenu';
import { rasterizeLayer } from './app/rasterize';
import { flattenImage } from './app/flatten';
import { mergeSelectedLayers } from './app/merge-layers';
import { BLEND_MODE_GROUPS } from './ui/layers/LayersPanel';
import { createSetAdjustmentCommand } from './app/commands/adjustment-commands';
import { AdjustmentPanel } from './ui/adjustments/AdjustmentPanel';
import { findLayer } from './document/draft';
import type { AdjustmentLayer, BlendMode, Guide, TextRun, VectorFill, VectorStroke } from './document/types';
import { ColorPickerDialog } from './ui/dialogs/ColorPickerDialog';
import { installTestApi } from './app/test-api';
import { AntsOverlay } from './interaction/ants-overlay';
import { BrushToolController, type ToolSample } from './interaction/tools/brush-tool';
import { handleEyedropper, eyedropperCursor, sampleDocumentColor } from './interaction/tools/eyedropper';
import { QuickSelectToolController } from './interaction/tools/quick-select';
import { MagicWandToolController } from './interaction/tools/magic-wand-tool';
import { MagneticLassoToolController } from './interaction/tools/magnetic-lasso-tool';
import { GradientToolController } from './interaction/tools/gradient-tool';
import { PaintBucketToolController } from './interaction/tools/paint-bucket-tool';
import { HealToolController } from './interaction/tools/heal-tool';
import { ShapeToolController } from './interaction/tools/shape-tool';
import { TransformToolController } from './interaction/tools/transform-tool';
import { PlaceToolController } from './interaction/tools/place-tool';
import { MoveToolController } from './interaction/tools/move-tool';
import type { DecodedImage } from './io/codecs';
import { CropToolController } from './interaction/tools/crop-tool';
import { PerspectiveCropToolController } from './interaction/tools/perspective-crop-tool';
import { VanishingPointToolController } from './interaction/tools/vanishing-point-tool';
import { StampToolController } from './interaction/tools/stamp-tool';
import { RedEyeToolController } from './interaction/tools/red-eye-tool';
import { PatchToolController } from './interaction/tools/patch-tool';
import { PenToolController } from './interaction/tools/pen-tool';
import { TextToolController } from './interaction/tools/text-tool';
import { FontLibrary, fetchFontLoader } from './app/text/fonts';
import { rasterizeText, type TextSpecRun } from './app/text/raster';
import { parseFontMetadata, UserFontStore, type FontRecord } from './io/fonts';
import {
  isLocalFontAccessAvailable,
  queryLocalFontFamilies,
  type LocalFontHandle,
} from './io/fonts/local-access';
import { FontManagerDialog } from './ui/dialogs/FontManagerDialog';
import {
  createSetWritingModeCommand,
  createSetTextWarpCommand,
  createRemoveTextWarpCommand,
} from './app/commands/text-commands';
import { WarpTextDialog, type WarpTextDialogState } from './ui/dialogs/WarpTextDialog';
import { documentToScreen, type ViewCommand } from './interaction/viewport';
import { registerViewActions, type ZoomCommandKind } from './app/actions/view-actions';
import { addGuide, moveGuide, removeGuideAt } from './app/guides';
import {
  loadCompositeChannelSelection,
  loadLayerAlphaSelection,
  selectionLoadModeFromModifiers,
} from './app/selection-from-source';
import { growSelection, similarSelection } from './app/selection-grow';
import { applyColorRangeSelection } from './app/color-range';
import { selectSubjectAction } from './app/selection-subject';
import { deletePath, duplicatePath, fillPath, loadPathSelection, renamePath } from './app/paths';
import { strokePathOnLayer } from './app/path-stroke';
import { flattenPath } from './engine/path/flatten';
import {
  FreehandGesture,
  PolygonGesture,
  RectGesture,
  applyCombine,
  applyMarqueeStyle,
  combineFromModifiers,
  lassoMask,
  marqueeMask,
  type MarqueeStyle,
  type SelectionCombine,
} from './interaction/tools/marquee';
import { createRenderBridge } from './interaction/render-bridge';
import { GpuCompositor } from './app/gpu/compositor';
import {
  ToolManager,
  isEditableTarget,
  TOOL_GROUPS,
  type ToolDefinition,
  type ToolGroupMember,
} from './interaction/tools/registry';
import { Toolbar } from './ui/toolbar/Toolbar';
import { OptionsBar } from './ui/toolbar/OptionsBar';
import toolbarStyles from './ui/toolbar/Toolbar.module.css';
import { LayersPanel } from './ui/layers/LayersPanel';
import { LayerChannelPathTabs } from './ui/panels/LayerChannelPathTabs';
import { ChannelsPanel, type ChannelRowId } from './ui/channels/ChannelsPanel';
import { PathsPanel, type PathRow } from './ui/paths/PathsPanel';
import { LayerCompsPanel } from './ui/layers/LayerCompsPanel';
import {
  createAddLayerCompCommand,
  createApplyLayerCompCommand,
  createRemoveLayerCompCommand,
  snapshotLayerComp,
} from './app/commands/layer-comp-commands';
import { ActionsPanel } from './ui/actions/ActionsPanel';
import { HistoryPanel } from './ui/history/HistoryPanel';
import { ScriptPanel } from './ui/scripting/ScriptPanel';
import { ScriptSandbox } from './app/scripting/sandbox';
import {
  ActionRecorder,
  deserializeAction,
  replayAction,
  serializeAction,
  type RecordedAction,
} from './app/actions/action-recorder';
import { runBatch } from './app/batch';
import {
  createSetBlendModeCommand,
  createSetCollapsedCommand,
  createSetEffectsCommand,
  createSetOpacityCommand,
  createSetVisibleCommand,
} from './app/commands/layer-commands';
import { TILE, layerId, tileKey } from './engine/tile/key';
import { createReorderLayerCommand, createRenameLayerCommand } from './app/commands/layer-commands';
import { deleteLayer } from './app/layer-edit';
import { createSetClippedCommand } from './app/commands/mask';
import { selectGateway, OpfsWorkspace } from './io/gateway';
import {
  createBrowserEnvironment,
  ensureCrossOriginIsolation,
} from './platform/cross-origin-isolation';
import { describeWasmFeatures, detectWasmFeatures } from './platform/wasm-features';
import { defaultPoolSize } from './workers/pool';

/**
 * 自由变换会话在属性栏里显示的宽/高/等比——跟"置入"共用同一套控件样式，
 * 但变换会话不是靠切工具驱动的（Ctrl+T 在任何工具选中时都能触发），
 * 不进 TOOL_REGISTRY/TOOL_GROUPS，只是给 OptionsBar 用的静态展示数据。
 */
const TRANSFORM_TOOL_DISPLAY: ToolDefinition = {
  id: 'transform',
  label: '自由变换',
  key: '仅程序触发',
  glyph: 'frame',
  cursor: 'default',
  options: [
    { kind: 'number', key: 'width', label: '宽度', min: 1, max: 20000, step: 1, defaultValue: 100, unit: 'px' },
    { kind: 'number', key: 'height', label: '高度', min: 1, max: 20000, step: 1, defaultValue: 100, unit: 'px' },
    { kind: 'boolean', key: 'uniform', label: '等比', defaultValue: true },
  ],
};

/**
 * 形状属性栏按当前「类型」过滤选项：直线工具自己的「粗细」定义线宽，
 * 跟矩形/椭圆的「描边宽度」是两套不该同时出现的东西（真实 PS 也是切开
 * 展示的，不会两个粗细字段一起摆出来让人猜哪个管什么）。
 */
function shapeToolOptionsForKind(base: ToolDefinition, kind: string): ToolDefinition {
  // "半径"只对矩形有意义（椭圆没有"角"，直线也没有），跟"粗细"/"描边"
  // 那两组的过滤逻辑分开判断——矩形要同时隐藏"粗细"（那是直线专属）
  // 又要显示"半径"，用三元不好扩展，直接按 kind 各自算一份隐藏列表
  const hiddenKeys: readonly string[] =
    kind === 'line' ? ['strokeWidth', 'strokeGradient', 'radius'] : kind === 'ellipse' ? ['lineWeight', 'radius'] : ['lineWeight'];
  return { ...base, options: base.options.filter((option) => !hiddenKeys.includes(option.key)) };
}

/**
 * 选框属性栏按当前「形状」「样式」过滤选项：椭圆没有"角"，不该出现
 * "半径"栏；样式不是"固定比例/固定大小"时，"宽度/高度"两栏没有意义
 * （那两栏是给固定比例/大小用的，"正常"样式下拖多大就是多大，没有
 * 数值要填）。
 */
function marqueeToolOptionsForShape(base: ToolDefinition, shape: string, style: string): ToolDefinition {
  const hiddenKeys: readonly string[] = [
    ...(shape === 'ellipse' ? ['radius'] : []),
    ...(style === 'normal' ? ['styleWidth', 'styleHeight'] : []),
  ];
  if (hiddenKeys.length === 0) return base;
  return { ...base, options: base.options.filter((option) => !hiddenKeys.includes(option.key)) };
}

/** 套索属性栏"宽度/对比/频率"三项只在「磁性」模式下有意义——自由/多边形
 * 套索没有边缘吸附这回事，摆出来也没用。 */
const LASSO_MAGNETIC_ONLY_KEYS: readonly string[] = ['magneticWidth', 'magneticContrast', 'magneticFrequency'];

function lassoToolOptionsForMode(base: ToolDefinition, mode: string): ToolDefinition {
  if (mode === 'magnetic') return base;
  return { ...base, options: base.options.filter((option) => !LASSO_MAGNETIC_ONLY_KEYS.includes(option.key)) };
}

/** 钢笔在「形状」模式下借用形状工具的填充/描边/宽度选项——真实 PS 里
 * 所有能画形状的工具共用同一份属性栏样式设置，不是各自独立一套。 */
const PEN_SHARED_SHAPE_OPTION_KEYS: readonly string[] = ['fillGradient', 'strokeWidth', 'strokeGradient'];

function penToolOptionsWithShape(pen: ToolDefinition, shapeTool: ToolDefinition): ToolDefinition {
  const shared = shapeTool.options.filter((option) => PEN_SHARED_SHAPE_OPTION_KEYS.includes(option.key));
  return { ...pen, options: [...pen.options, ...shared] };
}

/**
 * 新手引导的步骤（M5-05）——挑 Photoshop 用户下意识会找的几个地标：
 * 工具箱、图层面板、历史记录、"文件"菜单（导出的入口）。地标选择器要
 * 跟对应组件里真实挂着的 `data-testid` 一致，不是凭印象编的。
 */
const TOUR_STEPS: readonly TourStep[] = [
  {
    target: '[data-testid="toolbar"]',
    title: '工具箱',
    body: '常用工具都在这里，键位跟 Photoshop 一致——V 移动、B 画笔、T 文字……',
  },
  {
    target: '[data-testid="layers-panel"]',
    title: '图层面板',
    body: '管理图层、蒙版、混合模式的地方，右键一行还有更多操作。',
  },
  {
    target: '[data-testid="history-list"]',
    title: '历史记录',
    body: '每一步操作都记在这里，点回任意一步就是撤销到那里。',
  },
  {
    target: '[data-testid="menu-group-文件"]',
    title: '导出',
    body: '编辑完成后，"文件"菜单里有导出成 PNG/JPEG 等格式的入口。',
  },
];

const root = document.querySelector('#app');

async function boot(): Promise<void> {
  const isolation = await ensureCrossOriginIsolation(
    createBrowserEnvironment(import.meta.env.BASE_URL),
  );
  // 页面即将被刷新，继续初始化只会做无用功。
  if (isolation.reloadRequested) return;

  /* ---------------- 应用层装配 ---------------- */

  const store = new AppStore();
  const registry = new ActionRegistry();
  // 用户快捷键覆盖层（M5-03）：叠在 registry 每个动作的出厂默认 shortcut
  // 之上，菜单/命令面板/键盘事件匹配三处消费方都读"套过覆盖层之后的
  // 有效动作列表"（`effectiveActions()`，下面定义），不是分别接一份
  // overrides 参数——避免这一层到处传染
  const shortcutOverrides = new ShortcutOverrideStore();

  // 新手引导（M5-05）：只在"这台设备上从没看完/跳过过"时自动出现一次。
  // 复用 M5-03 快捷键覆盖层同一套 `io/kv-store.ts` 基础设施，不是
  // localStorage——跟这个应用其余持久化状态（快捷键覆盖、`.cvsm` 崩溃
  // 恢复镜像）同一条存储路径，不用另外引入一种机制
  const onboardingKv = await openKvStore('photosmith-onboarding');
  const [onboardingOpen, setOnboardingOpen] = createSignal(false);
  void onboardingKv.get<boolean>('seen').then((seen) => {
    if (seen !== true) setOnboardingOpen(true);
  });
  const dismissOnboarding = (): void => {
    setOnboardingOpen(false);
    void onboardingKv.set('seen', true);
  };

  let layerSeq = 0;
  registerStandardActions(registry, {
    store,
    tiles: store.tiles,
    nextLayerId: () => `layer-${Date.now().toString(36)}-${++layerSeq}`,
    beginTransform: () => transformController.begin(),
    setActiveLayer: (id) => {
      store.setActiveLayer(id);
    },
  });
  registerTransformActions(registry, {
    beginConstrained: (mode) => transformController.begin(mode),
    rotate90: (direction) => transformController.rotateQuick(direction),
    rotate180: () => transformController.rotate180Quick(),
    flip: (axis) => transformController.flipQuick(axis),
  });
  const clipboard = new ClipboardGateway(createBrowserClipboardDeps());
  registerClipboardActions(registry, {
    store,
    clipboard,
    tiles: store.tiles,
    nextLayerId: () => `layer-${Date.now().toString(36)}-${++layerSeq}`,
    setActiveLayer: (id) => {
      store.setActiveLayer(id);
    },
  });

  const { gateway, reason } = selectGateway();
  const workspace = await OpfsWorkspace.open();
  // 「文件 → 新建」先弹对话框（名称/宽高/单位/背景色，参照 Photoshop），
  // 真正建文档是确定之后的事——名称、背景色都是对话框自己给的，这里不用
  // 再维护一个"未命名-N"计数器
  const [newDocumentOpen, setNewDocumentOpen] = createSignal(false);
  // 场景卡片进入时预选的模板类目；「文件 → 新建」走 onRequestNewDocument，
  // 不设类目，两条路径共用同一个对话框但互不干扰
  const [newDocumentCategoryId, setNewDocumentCategoryId] = createSignal<string | undefined>(undefined);
  const openNewDocumentWithCategory = (categoryId: string): void => {
    setNewDocumentCategoryId(categoryId);
    setNewDocumentOpen(true);
  };
  const fileDeps = {
    store,
    gateway,
    workspace,
    now: () => new Date().toISOString(),
    onRequestNewDocument: () => {
      setNewDocumentCategoryId(undefined);
      setNewDocumentOpen(true);
    },
  };
  const fileSession = registerFileActions(registry, fileDeps);

  // 批处理可视化（场景化入口+批处理可视化那次工作）：面板/空状态卡片都只
  // 负责把「要跑哪个动作」的信息交过来，真正的文件选择/编码/保存/进度
  // 展示全部在 BatchDialog 内部，main.tsx 不再直连 gateway.open
  const [batchDialogOpen, setBatchDialogOpen] = createSignal(false);
  const [batchAction, setBatchAction] = createSignal<RecordedAction | null>(null);

  // 导出面板（M2-19）：多格式/多尺寸一次导出，动作只负责开面板
  const [exportOpen, setExportOpen] = createSignal(false);
  const [exportSummary, setExportSummary] = createSignal<string | undefined>(undefined);

  // 导出预览（参照 PS「导出为」的实时预览+体积估算）：格式/质量/颜色数/
  // 仿色任一变化，面板会经 onPreviewRequest 通知过来，这里做防抖再真的
  // 跑一遍编码——用真实字节数而不是估个大概，跟最终导出结果不会对不上。
  const [previewUrl, setPreviewUrl] = createSignal<string | undefined>(undefined);
  const [previewSize, setPreviewSize] = createSignal<string | undefined>(undefined);
  const [previewPending, setPreviewPending] = createSignal(false);
  let previewObjectUrl: string | null = null;
  let previewTimer: ReturnType<typeof setTimeout> | undefined;
  /** 预览缩略图的长边上限（像素）——大文档也不必按原尺寸编码一遍来估体积。 */
  const PREVIEW_MAX_DIM = 256;

  const formatBytes = (n: number): string => (n < 1024 ? `${n} B` : `约 ${(n / 1024).toFixed(1)} KB`);

  /**
   * 合成当前文档并缩到预览用的小尺寸——导出预览、色阶/曲线/曝光度/
   * 色相饱和度这几个破坏性调整对话框的实时预览全共用这一步，用浏览器
   * 原生 canvas 缩放就够了（不是最终落盘结果，不需要 wasm Lanczos 那套
   * 精细重采样）。没有文档时返回 null。
   */
  const downsizedCompositePixels = (
    maxDim: number,
  ): { pixels: Uint8Array; width: number; height: number } | null => {
    const doc = store.document;
    if (doc === null) return null;
    const flat = compositeDocument(doc, store.tiles);
    const scale = Math.min(1, maxDim / Math.max(flat.width, flat.height));
    const width = Math.max(1, Math.round(flat.width * scale));
    const height = Math.max(1, Math.round(flat.height * scale));

    const source = document.createElement('canvas');
    source.width = flat.width;
    source.height = flat.height;
    source.getContext('2d')!.putImageData(new ImageData(new Uint8ClampedArray(flat.pixels), flat.width, flat.height), 0, 0);
    const target = document.createElement('canvas');
    target.width = width;
    target.height = height;
    const targetCtx = target.getContext('2d')!;
    targetCtx.drawImage(source, 0, 0, width, height);
    const pixels: Uint8Array = new Uint8Array(targetCtx.getImageData(0, 0, width, height).data);
    return { pixels, width, height };
  };

  /**
   * 色阶/曲线/曝光度/色相饱和度对话框的实时预览：把 `transform` 套到
   * 缩略图每个像素上，直接画到 canvas 用 `toDataURL` 出图——跟导出预览
   * 不同，这里只是给眼睛看，不需要真的编码一遍，同步算完就行，不用防抖。
   */
  const computeAdjustmentPreviewUrl = (transform: PixelColorTransform): string | undefined => {
    const composited = downsizedCompositePixels(PREVIEW_MAX_DIM);
    if (composited === null) return undefined;
    const { pixels, width, height } = composited;
    const out = new Uint8ClampedArray(pixels.length);
    for (let i = 0; i < pixels.length; i += 4) {
      const [r, g, b, a] = transform(pixels[i]!, pixels[i + 1]!, pixels[i + 2]!, pixels[i + 3]!);
      out[i] = r;
      out[i + 1] = g;
      out[i + 2] = b;
      out[i + 3] = a;
    }
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    canvas.getContext('2d')!.putImageData(new ImageData(out, width, height), 0, 0);
    return canvas.toDataURL('image/png');
  };

  const requestExportPreview = (recipe: ExportPreviewRecipe): void => {
    if (previewTimer !== undefined) clearTimeout(previewTimer);
    setPreviewPending(true);
    previewTimer = setTimeout(() => {
      void (async () => {
        const composited = downsizedCompositePixels(PREVIEW_MAX_DIM);
        if (composited === null) return;
        const { width, height } = composited;
        let pixels = composited.pixels;

        let bytes: Uint8Array;
        let mime: string;
        if (recipe.format === 'png8') {
          const { indices, palette } = buildIndexedImage(pixels, width, height, recipe.colors, recipe.dither);
          bytes = await encodeIndexedPng(indices, palette, width, height);
          mime = 'image/png';
        } else if (recipe.format === 'jpeg') {
          pixels = flattenToWhite(pixels);
          bytes = await encodeImage('jpeg', pixels, { width, height, quality: recipe.quality });
          mime = 'image/jpeg';
        } else {
          bytes = await encodeImage('png', pixels, { width, height });
          mime = 'image/png';
        }

        if (previewObjectUrl !== null) URL.revokeObjectURL(previewObjectUrl);
        previewObjectUrl = URL.createObjectURL(new Blob([bytes as BlobPart], { type: mime }));
        setPreviewUrl(previewObjectUrl);
        setPreviewSize(formatBytes(bytes.length));
        setPreviewPending(false);
      })();
    }, 250);
  };
  registry.register({
    id: 'file.export',
    title: '导出…',
    group: '文件',
    section: 1,
    order: 2,
    enabled: (s) => s.hasDocument,
    run: () => {
      setExportSummary(undefined);
      setExportOpen(true);
    },
  });

  registerEditActions(registry, { store });

  // 填充对话框（编辑→「填充…」，Shift+F5 跟 Photoshop 一致）：填充方式/
  // 混合模式/不透明度都在对话框里选，跟 edit.fillForeground（Alt+Delete
  // 直接拿前景色填、不问混合模式）是两条并存的路径，不互相替代
  const [fillDialogOpen, setFillDialogOpen] = createSignal(false);
  const [fillBusy, setFillBusy] = createSignal(false);
  registry.register({
    id: 'edit.fillDialog',
    title: '填充…',
    group: '编辑',
    section: 2,
    order: 3,
    shortcut: { key: 'F5', shift: true },
    // 无选区也能打开——大多数填充方式（前景色/背景色/颜色…）没有选区时
    // 填满整个活动图层，真实 Photoshop 一样；只有"内容识别"这一个选项
    // 真的需要选区（见下面 onFill 里的提示），不该因为它卡住整个对话框
    enabled: (s) => s.hasDocument,
    run: () => {
      setFillDialogOpen(true);
    },
  });

  // 描边对话框（编辑→「描边…」）：宽度/颜色/位置在对话框里选，没有选区时
  // 描整个画布边缘——同填充对话框"没选区就整层"的回退（见 stroke-edit.ts）
  const [strokeDialogOpen, setStrokeDialogOpen] = createSignal(false);
  registry.register({
    id: 'edit.stroke',
    title: '描边…',
    group: '编辑',
    section: 2,
    order: 5,
    enabled: (s) => s.hasDocument,
    run: () => {
      setStrokeDialogOpen(true);
    },
  });

  // "图像"菜单的破坏性调整（跟"图层→新建调整图层"是两条不同的路，
  // 见 app/color-adjust.ts 头注释）：反相/去色即时生效；色阶/曲线/
  // 曝光度/色相饱和度需要参数，先开对话框，「确定」才真的落地一条历史
  const [levelsOpen, setLevelsOpen] = createSignal(false);
  const [curvesOpen, setCurvesOpen] = createSignal(false);
  const [exposureOpen, setExposureOpen] = createSignal(false);
  const [hueSaturationOpen, setHueSaturationOpen] = createSignal(false);
  const [levelsPreview, setLevelsPreview] = createSignal<string | undefined>(undefined);
  const [curvesPreview, setCurvesPreview] = createSignal<string | undefined>(undefined);
  const [exposurePreview, setExposurePreview] = createSignal<string | undefined>(undefined);
  const [hueSaturationPreview, setHueSaturationPreview] = createSignal<string | undefined>(undefined);

  const levelsTransform = (params: LevelsParams): PixelColorTransform => {
    const lut = levelsLut(params);
    return (r, g, b, a) => [lut[r]!, lut[g]!, lut[b]!, a];
  };
  const curvesTransform = (points: readonly CurvePoint[]): PixelColorTransform => {
    const lut = curvesLut(points);
    return (r, g, b, a) => [lut[r]!, lut[g]!, lut[b]!, a];
  };
  const exposureTransform = (exposure: number): PixelColorTransform => {
    const lut = basicToneLut({ ...DEFAULT_BASIC_PARAMS, exposure });
    return (r, g, b, a) => [lut[r]!, lut[g]!, lut[b]!, a];
  };
  const hueSaturationTransform = (params: HslParams): PixelColorTransform => (r, g, b, a) => {
    const [nr, ng, nb] = adjustHsl(r, g, b, params);
    return [nr, ng, nb, a];
  };

  const [canvasSizeOpen, setCanvasSizeOpen] = createSignal(false);
  const [imageSizeOpen, setImageSizeOpen] = createSignal(false);
  const [imageSizeBusy, setImageSizeBusy] = createSignal(false);

  registerImageActions(registry, {
    store,
    onRequestLevels: () => setLevelsOpen(true),
    onRequestCurves: () => setCurvesOpen(true),
    onRequestExposure: () => setExposureOpen(true),
    onRequestHueSaturation: () => setHueSaturationOpen(true),
    onRequestCanvasSize: () => setCanvasSizeOpen(true),
    onRequestImageSize: () => setImageSizeOpen(true),
  });

  let adjustmentSeq = 0;
  registerAdjustmentActions(registry, {
    store,
    gateway,
    nextLayerId: () => `adjust-${Date.now().toString(36)}-${++adjustmentSeq}`,
  });
  const [filterDialogKind, setFilterDialogKind] = createSignal<FilterKind | null>(null);
  registerFilterActions(registry, {
    store,
    openDialog: (kind) => {
      setFilterDialogKind(kind);
    },
  });
  registerEffectActions(registry, { store });

  // 图层样式对话框（DESIGN.md §7.1 补充：PS 完整 10 种效果）
  const [layerStyleTargetId, setLayerStyleTargetId] = createSignal<string | null>(null);
  const nextGeneratedLayerId = (): string => `layer-${Date.now().toString(36)}-${++layerSeq}`;
  registry.register({
    id: 'layer.style',
    title: '图层样式…',
    group: '图层',
    section: 3,
    order: 2,
    enabled: (s) => s.hasActiveLayer,
    run: () => {
      const id = store.activeLayerId;
      if (id !== null) setLayerStyleTargetId(id as unknown as string);
    },
  });

  // 图层面板右键菜单（DESIGN.md §7.1 补充）
  const [contextMenu, setContextMenu] = createSignal<{ x: number; y: number; layerId: string } | null>(null);
  /** 画布右键菜单（参照 Photoshop 选框/套索工具右键弹出的选区快捷菜单）。 */
  const [canvasContextMenu, setCanvasContextMenu] = createSignal<{ x: number; y: number } | null>(null);

  // 通道面板（DESIGN.md §7.1 补充）：当前查看哪个通道，'rgb' = 正常视图。
  // 不用 createEffect 订阅这个信号去同步渲染桥——这一段还在 `render()`
  // 建立 Solid root 之前执行，这里起的 createEffect 没有 owner 收着，
  // dev 模式会警告"never be disposed"。选中通道只有一个改变的入口
  // （下面 ChannelsPanel 的 onSelectChange），直接在那个点击处理器里
  // 命令式地把两件事一起做掉即可，不需要声明式订阅
  const [activeChannel, setActiveChannel] = createSignal<ChannelRowId>('rgb');

  // 路径面板（DESIGN.md §7.1 补充，ADR-0011）：当前选中哪一条已保存路径
  // （驱动画布上的轮廓预览覆盖层，见下面 drawSelectedPathOverlay）
  const [selectedPathId, setSelectedPathId] = createSignal<string | null>(null);
  let pathSeq = 0;
  const nextGeneratedPathId = (): string => `path-${Date.now().toString(36)}-${++pathSeq}`;
  const pathRows = (): readonly PathRow[] => {
    version();
    return (store.document?.paths ?? []).map((p) => ({ id: p.id, name: p.name }));
  };
  /** 选中路径的轮廓预览——跟钢笔工具自己画锚点线（`pen-tool.ts#drawOverlay`）
   * 是两回事：这里没有锚点/控制柄，只是把已保存的 `PathData.commands`
   * 拍扁成折线画出来，纯粹是"你选的是这条"的视觉提示。 */
  const drawSelectedPathOverlay = (ctx: CanvasRenderingContext2D): void => {
    const id = selectedPathId();
    const found = id === null ? undefined : store.document?.paths.find((p) => p.id === id);
    if (found === undefined) return;
    ctx.save();
    ctx.strokeStyle = 'rgba(0, 160, 255, 0.9)';
    ctx.lineWidth = 1;
    for (const sub of flattenPath(found.path.commands)) {
      if (sub.points.length === 0) continue;
      ctx.beginPath();
      ctx.moveTo(sub.points[0]!.x, sub.points[0]!.y);
      for (let i = 1; i < sub.points.length; i++) ctx.lineTo(sub.points[i]!.x, sub.points[i]!.y);
      if (sub.closed) ctx.closePath();
      ctx.stroke();
    }
    ctx.restore();
  };

  const [seamCarveOpen, setSeamCarveOpen] = createSignal(false);
  const [seamCarveBusy, setSeamCarveBusy] = createSignal(false);
  registry.register({
    id: 'image.seamCarve',
    title: '内容识别缩放…',
    group: '图像',
    section: 0,
    order: 0,
    enabled: (s) => s.hasDocument,
    run: () => {
      setSeamCarveOpen(true);
    },
  });

  // 命令面板（M4-10）
  const [commandPaletteOpen, setCommandPaletteOpen] = createSignal(false);

  // 面板显隐（「窗口」菜单，PS 惯例）：动作/图层复合/脚本默认隐藏,
  // 常用三件套（图层/属性/历史）常驻——六个面板全常驻会把侧栏挤到只剩表头
  const [panelVisibility, setPanelVisibility] = createSignal<{
    actions: boolean;
    layerComps: boolean;
    script: boolean;
  }>({ actions: false, layerComps: false, script: false });
  const PANEL_TOGGLES = [
    { action: 'window.toggleActions', title: '动作', key: 'actions' },
    { action: 'window.toggleLayerComps', title: '图层复合', key: 'layerComps' },
    { action: 'window.toggleScript', title: '脚本', key: 'script' },
  ] as const;
  for (const [order, toggle] of PANEL_TOGGLES.entries()) {
    registry.register({
      id: toggle.action,
      title: toggle.title,
      group: '窗口',
      section: 0,
      order,
      run: () => {
        setPanelVisibility((v) => ({ ...v, [toggle.key]: !v[toggle.key] }));
      },
    });
  }

  // 标尺（「视图」菜单，Ctrl+R 跟 Photoshop 一致）
  const [showRulers, setShowRulers] = createSignal(true);
  registry.register({
    id: 'view.toggleRulers',
    title: '标尺',
    group: '视图',
    section: 0,
    order: 0,
    shortcut: { key: 'R', mod: true },
    run: () => {
      setShowRulers((v) => !v);
    },
  });

  // 缩放/参考线/对齐（「视图」菜单其余部分）。菜单点出来的缩放命令走
  // viewCommandRequest 这条额外路径转给 CanvasView——Ctrl+0/1/+/- 本身
  // 已经在 CanvasView 自己的 keydown 监听器里直接处理，两条路殊途同归
  // 都调 applyViewCommand，不会重复处理同一次按键（见 CanvasView.tsx 注释）。
  let viewCommandSeq = 0;
  const [viewCommandRequest, setViewCommandRequest] = createSignal<
    { command: ViewCommand; seq: number } | undefined
  >(undefined);
  const [guidesLocked, setGuidesLockedSignal] = createSignal(false);
  const [snapEnabled, setSnapEnabled] = createSignal(true); // PS 默认开启"对齐"
  const [newGuideOpen, setNewGuideOpen] = createSignal(false);
  registerViewActions(registry, {
    store,
    requestViewCommand: (kind: ZoomCommandKind) => {
      const command: ViewCommand = { kind };
      setViewCommandRequest({ command, seq: ++viewCommandSeq });
    },
    onRequestNewGuide: () => {
      setNewGuideOpen(true);
    },
    guidesLocked,
    setGuidesLockedState: setGuidesLockedSignal,
    snapEnabled,
    setSnapEnabled,
  });

  // 脚本沙箱（M4-09）
  const scriptSandbox = new ScriptSandbox({ store });
  const [scriptRunning, setScriptRunning] = createSignal(false);
  const [scriptLog, setScriptLog] = createSignal<string[]>([]);

  // 图层复合（M4-05）
  let layerCompSeq = 0;

  // 动作录制/批处理（M4-04）
  const actionRecorder = new ActionRecorder();
  let recordingName = '';
  const [isRecording, setIsRecording] = createSignal(false);
  const [recordedActions, setRecordedActions] = createSignal<RecordedAction[]>([]);
  const [lastSkipped, setLastSkipped] = createSignal<number | null>(null);

  // 色彩范围（选择→色彩范围…）：对话框开着时，画布点击被路由成"取样
  // 参考色"而不是走平常的工具分发（见 onToolPointer 靠前的拦截）；
  // colorRangeRefRgb/colorRangePreviousSelection 不用做成信号——它们只在
  // 事件回调内部读写，没有任何 JSX 直接绑定它们，包成信号只会多余地
  // 触发无关的重渲染。colorRangeSwatch 是唯一需要信号的（对话框的色块
  // 要跟着取样结果重绘）。
  const [colorRangeOpen, setColorRangeOpen] = createSignal(false);
  const [colorRangeSwatch, setColorRangeSwatch] = createSignal<string | null>(null);
  let colorRangeRefRgb: RgbColor | null = null;
  let colorRangePreviousSelection: SelectionMask | null = null;
  let colorRangeFuzziness = COLOR_RANGE_DEFAULT_FUZZINESS;
  let colorRangeInvertFlag = false;
  // 容差滑杆拖动时连续触发重算，每次都起一次新的异步计算——见
  // applyColorRangeSelection 的 isStale 参数注释：只有最新一次调用的
  // 结果允许真的写回 store，更早、还没算完就被后来的输入盖过去的调用
  // 得知自己过期后要作废，不然会出现选区被旧结果盖回去的倒退闪烁。
  let colorRangeGeneration = 0;

  const applyColorRangePreview = (): void => {
    if (colorRangeRefRgb === null) return;
    const { r, g, b } = colorRangeRefRgb;
    const generation = ++colorRangeGeneration;
    void applyColorRangeSelection(
      store,
      [r, g, b],
      colorRangeFuzziness,
      colorRangeInvertFlag,
      colorRangePreviousSelection,
      () => generation !== colorRangeGeneration,
    );
  };

  registerSelectionActions(registry, {
    selection: () => store.selection,
    setSelection: (mask) => {
      store.setSelection(mask);
    },
    documentSize: () => store.document?.size ?? null,
    // 跟 PS 一致：没有对话框，直接用当前魔棒工具设的容差
    grow: () => growSelection(store, toolManager.getOption('magicWand', 'tolerance') as number),
    similar: () => similarSelection(store, toolManager.getOption('magicWand', 'tolerance') as number),
    subject: () => selectSubjectAction(store),
    colorRange: () => {
      colorRangePreviousSelection = store.selection;
      colorRangeRefRgb = null;
      colorRangeFuzziness = COLOR_RANGE_DEFAULT_FUZZINESS;
      colorRangeInvertFlag = false;
      setColorRangeSwatch(null);
      setColorRangeOpen(true);
    },
  });

  // 选择→修改（边界/平滑/扩展/收缩/羽化）：五项共用一个「输入像素数」对话框，
  // 靠 modifyDialogKind 记这次点的是哪一种（见 selection-modify-actions.ts）
  const [modifyDialogKind, setModifyDialogKind] = createSignal<SelectionModifyKind | null>(null);
  registerSelectionModifyActions(registry, {
    openDialog: (kind) => {
      setModifyDialogKind(kind);
    },
  });

  const [aboutOpen, setAboutOpen] = createSignal(false);
  registry.register({
    id: 'help.about',
    title: '关于 Photosmith…',
    group: '帮助',
    section: 0,
    order: 0,
    run: () => {
      setAboutOpen(true);
    },
  });

  // 帮助面板（M5-05 / DESIGN.md §7.5）——常用操作速查 + 隐私说明入口，
  // 排在"关于"前面（section 号更小），跟真实 Photoshop"帮助"菜单里
  // 帮助内容排在关于/许可证之前的顺序一致
  const [helpPanelOpen, setHelpPanelOpen] = createSignal(false);
  registry.register({
    id: 'help.quickHelp',
    title: '帮助…',
    group: '帮助',
    section: 0,
    order: -1,
    run: () => {
      setHelpPanelOpen(true);
    },
  });

  // 键盘快捷键编辑器（M5-03 / DESIGN.md §7.4）——真实 Photoshop 放在"编辑"
  // 菜单最后一节，独立一个新 section 而不是塞进已有的两节，跟填充/描边
  // 那种"作用于文档内容"的动作分开——这个动作跟有没有打开文档无关
  const [shortcutEditorOpen, setShortcutEditorOpen] = createSignal(false);
  registry.register({
    id: 'edit.keyboardShortcuts',
    title: '键盘快捷键…',
    group: '编辑',
    section: 3,
    order: 0,
    run: () => {
      setShortcutEditorOpen(true);
    },
  });

  const services: ActionServices = {
    state: () => store.state(),
    document: () => store.document,
    dispatch: (command) => {
      store.dispatch(command);
    },
    undo: () => {
      store.undo();
    },
    redo: () => {
      store.redo();
    },
    hint: (text) => {
      store.setHint(text);
    },
  };

  /* ---------------- Solid 桥接 ---------------- */

  // store 是普通对象，靠一个版本号信号把变化引进 Solid 的响应式世界
  const [version, setVersion] = createSignal(0);
  store.subscribe(() => setVersion((v) => v + 1));
  shortcutOverrides.subscribe(() => setVersion((v) => v + 1));

  /** 套过用户快捷键覆盖层的动作列表——菜单/命令面板/键盘匹配三处共用，
   * 避免各自分别接 `overrides` 参数。读 `version()` 保证覆盖层加载完成
   * （异步从 IndexedDB 读回）或改动之后这里能重新算一遍。 */
  const effectiveActions = (): ReturnType<typeof registry.list> => {
    version();
    return withEffectiveShortcuts(registry.list(), shortcutOverrides.overrides);
  };

  // GPU 显示路径（M3-09/10）：两条后端都不可用，或遇到 ADR-0005 未覆盖的
  // 内容时，渲染桥自动回退 CPU 合成桥——这里只负责把可用的合成器递进去
  const gpuCompositor = await GpuCompositor.create();
  const bridge = createRenderBridge(store, {
    gpu: gpuCompositor,
    requestRender: () => setVersion((v) => v + 1),
  });
  installTestApi(store);

  // 未捕获异常的可见化：这个应用的错误此前一律沉进控制台（用户报告
  // "打开图片变灰/变黑却没有任何提示"时完全无从自查）。状态栏是唯一
  // 常驻的信息面，任何未捕获异常/未处理 rejection 都打到那里——
  // 不吞错误（照常进控制台），只是让不开 DevTools 的用户也看得见。
  window.addEventListener('error', (event) => {
    store.setHint(`运行时错误：${event.message}`);
  });
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    store.setHint(`未处理的异步错误：${reason instanceof Error ? reason.message : String(reason)}`);
  });

  /**
   * 拖放导入（补充：DESIGN.md §2.6/§6.1）。dragover 必须 preventDefault，
   * 否则浏览器会用它自己的默认行为处理这次拖放（离开当前页面去"打开"
   * 那个文件），drop 事件永远等不到。
   */
  window.addEventListener('dragover', (event) => {
    event.preventDefault();
  });
  window.addEventListener('drop', (event) => {
    event.preventDefault();
    const file = event.dataTransfer?.files[0];
    if (file === undefined) return;
    if (!isDroppableImage(file.name)) {
      store.setHint(`不认识这个格式：${file.name}`);
      return;
    }
    void file
      .arrayBuffer()
      .then((buffer) =>
        importDroppedFile(
          store,
          { bytes: new Uint8Array(buffer), name: file.name },
          {
            tiles: store.tiles,
            now: () => new Date().toISOString(),
            beginPlace: (fileName, image, docWidth, docHeight) => {
              beginPlace(fileName, image, docWidth, docHeight);
            },
          },
        ),
      )
      .catch((error) => {
        store.setHint(`导入失败：${error instanceof Error ? error.message : String(error)}`);
      });
  });

  const toolManager = new ToolManager();
  const [toolVersion, setToolVersion] = createSignal(0);
  toolManager.subscribe(() => setToolVersion((v) => v + 1));
  const activeTool = (): ToolDefinition => {
    toolVersion();
    return toolManager.active;
  };

  /** 吸管悬停处采样到的颜色——只用来画光标，不进前景（前景只在按下时写入）。 */
  const [eyedropperColor, setEyedropperColor] = createSignal<RgbColor | null>(null);
  const cursorForActiveTool = (): string => {
    const tool = activeTool();
    if (tool.id === 'eyedropper') return eyedropperCursor(eyedropperColor());
    if (tool.id === 'zoom') {
      toolVersion();
      return (toolManager.getOption('zoom', 'zoomOut') as boolean) ? 'zoom-out' : 'zoom-in';
    }
    return tool.cursor;
  };

  const menuModel = (): ReturnType<typeof buildMenuModel> => {
    version();
    return buildMenuModel(effectiveActions(), store.state());
  };
  const documentSize = (): { width: number; height: number } => {
    version();
    return store.document?.size ?? { width: 3840, height: 2160 };
  };
  const guides = (): readonly Guide[] => {
    version();
    return store.document?.guides ?? [];
  };
  const hasDocument = (): boolean => {
    version();
    return store.state().hasDocument;
  };
  const layersDocument = (): typeof store.document => {
    version();
    return store.document;
  };
  const activeLayerId = (): string | null => {
    version();
    return store.activeLayerId;
  };
  const selectedLayerIds = (): ReadonlySet<string> => {
    version();
    return store.selectedLayerIds;
  };
  const historyEntries = (): readonly { readonly id: number; readonly label: string }[] => {
    version();
    return store.history?.entries.map((e) => ({ id: e.id, label: e.label })) ?? [];
  };
  const historyCursor = (): number => {
    version();
    return store.history?.cursor ?? -1;
  };
  const foregroundColor = (): typeof store.foreground => {
    version();
    return store.foreground;
  };
  const backgroundColor = (): typeof store.background => {
    version();
    return store.background;
  };
  const colorCss = (color: { r: number; g: number; b: number }): string =>
    `rgb(${color.r} ${color.g} ${color.b})`;
  /** `#rrggbb`（原生 `<input type="color">` 的格式）→ RgbColor，填充对话框的"颜色…"选项用。 */
  const hexToRgb = (hex: string): { r: number; g: number; b: number } => ({
    r: parseInt(hex.slice(1, 3), 16),
    g: parseInt(hex.slice(3, 5), 16),
    b: parseInt(hex.slice(5, 7), 16),
  });
  /** 反过来：RgbColor → `#rrggbb`，描边对话框拿前景色当默认颜色用。 */
  const rgbToHex = (color: { r: number; g: number; b: number }): string =>
    `#${[color.r, color.g, color.b].map((v) => v.toString(16).padStart(2, '0')).join('')}`;
  /**
   * 缩略图渲染（M3-03，通道面板补充复用）：按目标尺寸最近邻采样，调用方
   * 给一个"这个缩略像素该取哪个源像素"的采样函数即可——图层缩略图（采样
   * 单图层 tile）和通道缩略图（采样整份文档合成结果的某个通道）只是
   * `sample` 不同，画到 canvas、判断"整张全透明就不出图"这部分完全一样，
   * 不该各写一份。
   */
  const THUMB_W = 40;
  const THUMB_H = 30;
  const renderThumbnail = (
    sample: (tx: number, ty: number) => readonly [number, number, number, number] | null,
  ): string | null => {
    const canvas = document.createElement('canvas');
    canvas.width = THUMB_W;
    canvas.height = THUMB_H;
    const ctx = canvas.getContext('2d');
    if (ctx === null) return null;
    const image = ctx.createImageData(THUMB_W, THUMB_H);
    let hasInk = false;
    for (let ty = 0; ty < THUMB_H; ty++) {
      for (let tx = 0; tx < THUMB_W; tx++) {
        const px = sample(tx, ty);
        if (px === null) continue;
        const dst = (ty * THUMB_W + tx) * 4;
        image.data[dst] = px[0];
        image.data[dst + 1] = px[1];
        image.data[dst + 2] = px[2];
        image.data[dst + 3] = px[3];
        if (px[3] > 0) hasInk = true;
      }
    }
    ctx.putImageData(image, 0, 0);
    return hasInk ? canvas.toDataURL() : null;
  };

  /** 图层缩略图：按目标尺寸最近邻采样 tile 数据。按 pixelEpoch 记忆化。 */
  const thumbCache = new Map<string, { key: string; url: string | null }>();
  const thumbnailFor = (id: string): string | null => {
    version();
    const doc = store.document;
    if (doc === null) return null;
    const found = findLayer(doc.root, layerId(id));
    if (found === null || (found.layer.kind !== 'raster' && found.layer.kind !== 'text')) {
      return null;
    }
    const key = `${doc.id}:${store.pixelEpoch}:${store.history?.entries.length ?? 0}`;
    const hit = thumbCache.get(id);
    if (hit !== undefined && hit.key === key) return hit.url;

    const url = renderThumbnail((tx, ty) => {
      const sx = Math.floor(((tx + 0.5) / THUMB_W) * doc.size.width);
      const sy = Math.floor(((ty + 0.5) / THUMB_H) * doc.size.height);
      const tile = store.tiles.get(
        tileKey(layerId(id), Math.floor(sx / TILE), Math.floor(sy / TILE)),
      );
      const data = tile?.data;
      if (data == null) return null;
      const src = ((sy % TILE) * TILE + (sx % TILE)) * 4;
      return [data[src]!, data[src + 1]!, data[src + 2]!, data[src + 3]!];
    });
    thumbCache.set(id, { key, url });
    return url;
  };

  /**
   * 通道缩略图（DESIGN.md §7.1 补充）：跟图层缩略图不一样，采样源是
   * **整份文档合成结果**（`compositeDocument`），不是单图层 tile——通道
   * 面板看的是"这份画面里的红/绿/蓝分量"，不是某一层自己的内容。
   * R/G/B 三行按各自通道值画成灰度；RGB 那一行就是正常合成缩略图。
   */
  const channelThumbCache = new Map<string, { key: string; url: string | null }>();
  const channelThumbnailFor = (channel: ChannelRowId): string | null => {
    version();
    const doc = store.document;
    if (doc === null) return null;
    const key = `${doc.id}:${store.pixelEpoch}:${store.history?.entries.length ?? 0}`;
    const hit = channelThumbCache.get(channel);
    if (hit !== undefined && hit.key === key) return hit.url;

    const composite = compositeDocument(doc, store.tiles);
    const url = renderThumbnail((tx, ty) => {
      const sx = Math.floor(((tx + 0.5) / THUMB_W) * composite.width);
      const sy = Math.floor(((ty + 0.5) / THUMB_H) * composite.height);
      const src = (sy * composite.width + sx) * 4;
      const r = composite.pixels[src]!;
      const g = composite.pixels[src + 1]!;
      const b = composite.pixels[src + 2]!;
      const a = composite.pixels[src + 3]!;
      if (channel === 'rgb') return [r, g, b, a];
      const v = channel === 'r' ? r : channel === 'g' ? g : b;
      return [v, v, v, 255];
    });
    channelThumbCache.set(channel, { key, url });
    return url;
  };
  // 活动图层是调整图层时给属性面板；否则 null（面板显示占位）
  const activeAdjustmentLayer = (): AdjustmentLayer | null => {
    version();
    const doc = store.document;
    const id = store.activeLayerId;
    if (doc === null || id === null) return null;
    const found = findLayer(doc.root, id);
    return found !== null && found.layer.kind === 'adjustment' ? found.layer : null;
  };
  const statusDocument = (): string | undefined => {
    version();
    const document = store.document;
    if (document === null) return undefined;
    return `${document.meta.title} · ${document.size.width}×${document.size.height}`;
  };
  const [capabilityHint, setCapabilityHint] = createSignal('');
  const hint = (): string => {
    version();
    return store.hint === '' ? capabilityHint() : store.hint;
  };

  /* ---------------- 选区工具手势 ---------------- */

  const rectGesture = new RectGesture();
  const freehand = new FreehandGesture();
  const polygon = new PolygonGesture();
  let gestureCombine: SelectionCombine = 'replace';
  /**
   * 矩形选框最近一次提交的原始参数（形状/矩形/合成模式/合成前的底
   * 选区/合成后的结果）——用户提交后立刻在属性栏改"半径"，不用重新拖
   * 一次就能让当前选区跟着变圆角，跟真实 Photoshop 手感一致。
   * `result` 只用来跟 `store.selection` 做引用比较：提交之后只要用户
   * 做过任何别的改动（拖新选框、撤销、其它工具提交……），selection
   * 会变成新对象，比较就会失配，判定"半径已经跟不上当前选区了"而
   * 直接跳过，不会误把半径套到不相关的选区上。
   */
  let lastMarqueeCommit: {
    shape: 'rectangle' | 'ellipse';
    rect: NonNullable<ReturnType<typeof rectGesture.rect>>;
    combine: SelectionCombine;
    base: SelectionMask | null;
    result: SelectionMask;
  } | null = null;
  /** 多边形套索"下一条边"的橡皮筋预览终点；只用于叠加层绘制，不参与选区计算。 */
  let polygonCursor: { x: number; y: number } | null = null;

  /**
   * 选框工具属性栏的"样式"选项算出来的实际矩形——"正常"就是原始拖拽
   * 范围，"固定比例/固定大小"按 marquee.ts#applyMarqueeStyle 调整。
   * 拖拽预览（drawMarqueeOverlay）跟真正提交（onToolPointer 的 'up'）
   * 共用这一个函数，两处必须看起来一致。
   */
  const currentMarqueeRect = (): ReturnType<typeof rectGesture.rect> => {
    const style = toolManager.getOption('marquee', 'style') as MarqueeStyle;
    if (style === 'normal') return rectGesture.rect();
    const start = rectGesture.start;
    const current = rectGesture.current;
    if (start === null || current === null) return null;
    const styleWidth = toolManager.getOption('marquee', 'styleWidth') as number;
    const styleHeight = toolManager.getOption('marquee', 'styleHeight') as number;
    return applyMarqueeStyle(start, current, style, styleWidth, styleHeight);
  };

  const commitSelection = (
    mask: ReturnType<typeof marqueeMask> | null,
  ): void => {
    if (mask === null) return;
    store.setSelection(applyCombine(store.selection, mask, gestureCombine));
  };

  /** 属性栏"半径"实时生效：见 lastMarqueeCommit 的说明。 */
  const applyMarqueeRadiusLive = (radius: number): void => {
    const doc = store.document;
    if (lastMarqueeCommit === null || doc === null || store.selection !== lastMarqueeCommit.result) {
      lastMarqueeCommit = null;
      return;
    }
    const { width, height } = doc.size;
    const mask = marqueeMask(lastMarqueeCommit.shape, width, height, lastMarqueeCommit.rect, radius);
    const result = applyCombine(lastMarqueeCommit.base, mask, lastMarqueeCommit.combine);
    store.setSelection(result);
    lastMarqueeCommit = { ...lastMarqueeCommit, result };
  };

  /**
   * 形状/文字的填充与描边（M3-15/M2-18 补充：跟 Photoshop 属性栏一致地
   * 补上颜色+描边颜色+描边宽度，且都支持渐变）。
   *
   * 渐变色标数组不是 ToolOptionValue 能装的类型（同渐变工具的既有理由），
   * 走这里的独立 signal，"用不用渐变"这个布尔开关才登记进
   * registry.ts 的 fillGradient/strokeGradient 选项。默认色标用中性的
   * 黑→白，不预设成任何看起来像"这才是正确答案"的配色。
   */
  const NEUTRAL_GRADIENT: readonly GradientStopInput[] = [
    { offset: 0, color: { r: 0, g: 0, b: 0 }, alpha: 1 },
    { offset: 1, color: { r: 255, g: 255, b: 255 }, alpha: 1 },
  ];
  // 形状工具的默认状态跟真实 Photoshop 一致：新选中工具时是「背景透明、
  // 黑色描边」，不是"实心灰"——不然每次选形状工具画出来的第一个图形都是
  // 一块灰底，不符合直觉。
  const [shapeFillColor, setShapeFillColor] = createSignal('#808080');
  const [shapeFillAlpha, setShapeFillAlpha] = createSignal(0);
  const [shapeFillStops, setShapeFillStops] = createSignal<readonly GradientStopInput[]>(NEUTRAL_GRADIENT);
  const [shapeStrokeColor, setShapeStrokeColor] = createSignal('#000000');
  const [shapeStrokeAlpha, setShapeStrokeAlpha] = createSignal(1);
  const [shapeStrokeStops, setShapeStrokeStops] = createSignal<readonly GradientStopInput[]>(NEUTRAL_GRADIENT);
  const [textFillColor, setTextFillColor] = createSignal('#000000');
  const [textFillAlpha, setTextFillAlpha] = createSignal(1);
  const [textFillStops, setTextFillStops] = createSignal<readonly GradientStopInput[]>(NEUTRAL_GRADIENT);
  const [textStrokeColor, setTextStrokeColor] = createSignal('#ffffff');
  const [textStrokeAlpha, setTextStrokeAlpha] = createSignal(1);
  const [textStrokeStops, setTextStrokeStops] = createSignal<readonly GradientStopInput[]>(NEUTRAL_GRADIENT);
  /** 眼下打开的是哪个色块的对话框；null = 都关着。 */
  type ColorEditTarget = 'shapeFill' | 'shapeStroke' | 'textFill' | 'textStroke';
  const [colorEditTarget, setColorEditTarget] = createSignal<ColorEditTarget | null>(null);
  /**
   * 工具栏前景/背景色块点击后要编辑哪一个；null = 没打开。跟上面 shape/text
   * 那套 ColorEditTarget 分开——前景/背景色只有纯色、没有渐变/透明度这些
   * 变体（跟真实 PS 的工具箱色块一致），不需要套那套 gradient 判断逻辑。
   */
  const [fgBgEditTarget, setFgBgEditTarget] = createSignal<'foreground' | 'background' | null>(null);
  /** 当前 target 是纯色还是渐变模式——决定弹出 ColorPickerDialog 还是 GradientEditorDialog。 */
  const colorEditTargetIsGradient = (): boolean => {
    switch (colorEditTarget()) {
      case 'shapeFill':
        return toolManager.getOption('shape', 'fillGradient') === true;
      case 'shapeStroke':
        return toolManager.getOption('shape', 'strokeGradient') === true;
      case 'textFill':
        return toolManager.getOption('text', 'fillGradient') === true;
      case 'textStroke':
        return toolManager.getOption('text', 'strokeGradient') === true;
      case null:
        return false;
    }
  };
  const colorEditTargetSolid = (): string => {
    switch (colorEditTarget()) {
      case 'shapeFill':
        return shapeFillColor();
      case 'shapeStroke':
        return shapeStrokeColor();
      case 'textFill':
        return textFillColor();
      case 'textStroke':
        return textStrokeColor();
      case null:
        return '#000000';
    }
  };
  const colorEditTargetSolidAlpha = (): number => {
    switch (colorEditTarget()) {
      case 'shapeFill':
        return shapeFillAlpha();
      case 'shapeStroke':
        return shapeStrokeAlpha();
      case 'textFill':
        return textFillAlpha();
      case 'textStroke':
        return textStrokeAlpha();
      case null:
        return 1;
    }
  };
  /**
   * 文字的颜色/字体改动统一入口——编辑中只改选区、没编辑就整层改样式
   * 这个路由判断收在 `TextToolController.applyStyle` 内部（M4-40e，收拢
   * 之前散落在这个文件四个调用点的 if/else，见 `state/PROGRESS.md`），
   * 这里只是薄薄一层类型标注，方便调用点不用各自写 `Partial<RunStyle>`。
   */
  const applyTextStylePatch = (
    patch: Partial<{ fontFamily: string; fontSize: number; fill: VectorFill; stroke: VectorStroke }>,
  ): void => {
    textController.applyStyle(patch);
  };
  const setColorEditTargetSolid = (hex: string, alpha: number): void => {
    switch (colorEditTarget()) {
      case 'shapeFill':
        setShapeFillColor(hex);
        setShapeFillAlpha(alpha);
        break;
      case 'shapeStroke':
        setShapeStrokeColor(hex);
        setShapeStrokeAlpha(alpha);
        break;
      case 'textFill':
        // 富文本混合格式（M4-38）：编辑中有选区时，颜色只作用于选中的
        // 那段文字，不是整段——跟属性栏默认值的更新（下面两行）互不影响。
        // ⚠️必须先 apply 再更新 `setTextFillColor`/`setTextFillAlpha`：
        // 没被"选区改样式"功能碰过的文字，`applyStyle` 内部会用
        // `textFillValue()`（也就是这两个信号）当"这段文字原来的颜色"
        // 兜底基准——如果先把信号改成了新颜色，基准和目标就变成同一个
        // 颜色，选区内外看起来就没区别了，整段被误认成"都在改成新颜色"
        // （用户反馈"改变颜色后，未选择的文字也变色了"排查到的真实 bug，
        // M4-40f）。这里直接用 hex/alpha 现算目标色，不经过那两个信号。
        applyTextStylePatch({ fill: { type: 'solid', color: hex, alpha } });
        setTextFillColor(hex);
        setTextFillAlpha(alpha);
        textController.refocus();
        break;
      case 'textStroke':
        // 同上：先 apply（现算目标描边，不经过下面要更新的信号），再更新
        // 属性栏默认值
        {
          const width = toolManager.getOption('text', 'strokeWidth') as number;
          if (width > 0) {
            applyTextStylePatch({ stroke: { fill: { type: 'solid', color: hex, alpha }, width } });
          }
        }
        setTextStrokeColor(hex);
        setTextStrokeAlpha(alpha);
        textController.refocus();
        break;
      case null:
        break;
    }
  };
  const colorEditTargetStops = (): readonly GradientStopInput[] => {
    switch (colorEditTarget()) {
      case 'shapeFill':
        return shapeFillStops();
      case 'shapeStroke':
        return shapeStrokeStops();
      case 'textFill':
        return textFillStops();
      case 'textStroke':
        return textStrokeStops();
      case null:
        return NEUTRAL_GRADIENT;
    }
  };
  const setColorEditTargetStops = (stops: readonly GradientStopInput[]): void => {
    switch (colorEditTarget()) {
      case 'shapeFill':
        setShapeFillStops(stops);
        break;
      case 'shapeStroke':
        setShapeStrokeStops(stops);
        break;
      case 'textFill':
        // 跟 `setColorEditTargetSolid` 同一个顺序要求：先 apply（现算目标
        // 渐变，不经过下面要更新的信号），再更新属性栏默认值（M4-40f）
        applyTextStylePatch({ fill: stopsToVectorFill(stops) });
        setTextFillStops(stops);
        textController.refocus();
        break;
      case 'textStroke':
        {
          const width = toolManager.getOption('text', 'strokeWidth') as number;
          if (width > 0) {
            applyTextStylePatch({ stroke: { fill: stopsToVectorFill(stops), width } });
          }
        }
        setTextStrokeStops(stops);
        textController.refocus();
        break;
      case null:
        break;
    }
  };

  /** 纯色色块预览：复用渐变预览的棋盘格+色层拼法（两个色标同色），
   * 不透明度低于 100% 时才看得出真的透出底色，不是纯色块糊弄一个数字。 */
  const solidSwatchBackgroundImage = (hex: string, alpha: number): string => {
    const rgb = hexToRgb(hex);
    return gradientPreviewBackgroundImage([
      { offset: 0, color: rgb, alpha },
      { offset: 1, color: rgb, alpha },
    ]);
  };
  const hexByte = (n: number): string => Math.round(Math.min(255, Math.max(0, n))).toString(16).padStart(2, '0');
  const stopsToVectorFill = (stops: readonly GradientStopInput[]): VectorFill => ({
    type: 'gradient',
    stops: stops.map((s) => ({
      offset: s.offset,
      color: `#${hexByte(s.color.r)}${hexByte(s.color.g)}${hexByte(s.color.b)}`,
    })),
  });
  const shapeFillValue = (): VectorFill =>
    toolManager.getOption('shape', 'fillGradient') === true
      ? stopsToVectorFill(shapeFillStops())
      : { type: 'solid', color: shapeFillColor(), alpha: shapeFillAlpha() };
  const shapeStrokeValue = (): VectorStroke | undefined => {
    // 直线工具自己的「粗细」就是它的线宽，跟矩形/椭圆的「描边」是两套不该
    // 同时存在的东西（真实 PS 的直线工具也没有独立的描边）——直线永远
    // 不套用这份描边，属性栏那边也据此把描边宽度/渐变描边这两栏隐藏掉
    if (toolManager.getOption('shape', 'kind') === 'line') return undefined;
    const width = toolManager.getOption('shape', 'strokeWidth') as number;
    if (width <= 0) return undefined;
    const fill =
      toolManager.getOption('shape', 'strokeGradient') === true
        ? stopsToVectorFill(shapeStrokeStops())
        : { type: 'solid' as const, color: shapeStrokeColor(), alpha: shapeStrokeAlpha() };
    return { fill, width };
  };
  const textFillValue = (): VectorFill =>
    toolManager.getOption('text', 'fillGradient') === true
      ? stopsToVectorFill(textFillStops())
      : { type: 'solid', color: textFillColor(), alpha: textFillAlpha() };
  const textStrokeValue = (): VectorStroke | undefined => {
    const width = toolManager.getOption('text', 'strokeWidth') as number;
    if (width <= 0) return undefined;
    const fill =
      toolManager.getOption('text', 'strokeGradient') === true
        ? stopsToVectorFill(textStrokeStops())
        : { type: 'solid' as const, color: textStrokeColor(), alpha: textStrokeAlpha() };
    return { fill, width };
  };

  const fonts = new FontLibrary();

  /** 把 `TextLayer.runs`（文档模型）逐条解析出 shaper，喂给 `rasterizeText`
   * 的 `TextSpec.runs`——任何一条用到的字体还没加载完成就整体返回 null，
   * 调用方按既有的"字体尚未加载完成"提示处理，不部分渲染。 */
  const specRunsFromTextRuns = (runs: readonly TextRun[]): TextSpecRun[] | null => {
    const specRuns: TextSpecRun[] = [];
    for (const run of runs) {
      const shaper = fonts.shaperFor(run.fontFamily);
      if (shaper === null) return null;
      specRuns.push({
        text: run.text,
        shaper,
        fontSize: run.fontSize,
        fill: run.fill,
        ...(run.stroke === undefined ? {} : { stroke: run.stroke }),
      });
    }
    return specRuns;
  };

  // 字体是运行时才知道全貌的（内置异步加载完成前只有兜底列表；用户上传/
  // 系统导入随时可能发生），用一个版本号让读它的地方（字体下拉、字体
  // 管理对话框的列表）知道该重新读一遍——跟这个文件里其余 `version()`/
  // `toolVersion()` 同一个手法
  const [fontVersion, setFontVersion] = createSignal(0);
  void fonts
    .loadBuiltins(fetchFontLoader(import.meta.env.BASE_URL))
    .then(() => setFontVersion((v) => v + 1))
    .catch((error: unknown) => {
      store.setHint(`内置字体加载失败：${error instanceof Error ? error.message : String(error)}`);
    });
  // 用户之前导入过的字体（OPFS 持久化）：应用启动时枚举一遍重新注册——
  // 字体的字节不会跨会话缓存在内存里，跟画布崩溃恢复不是一回事，
  // 这里只是把已经在磁盘上的字体重新读进 FontLibrary
  void UserFontStore.open().then(async (userFonts) => {
    if (userFonts === null) return;
    const { fonts: records } = await userFonts.list();
    for (const record of records) fonts.register(record);
    if (records.length > 0) setFontVersion((v) => v + 1);
  });

  const [fontManagerOpen, setFontManagerOpen] = createSignal(false);
  // 上一次"从系统字体导入"查询的结果——只存字节获取句柄，不是 Solid 状态，
  // 用户点某一款的"导入"时按族名查这个表拿真正的 blob() 函数
  let lastSystemFontQuery: readonly LocalFontHandle[] = [];

  const handleQuerySystemFonts = async (): Promise<readonly string[]> => {
    lastSystemFontQuery = await queryLocalFontFamilies();
    return lastSystemFontQuery.map((f) => f.family);
  };

  /**
   * 真正取字节、解析、注册，返回注册后的记录——调用方需要知道解析出来的
   * 真实族名（`record.family`），因为它未必跟 `queryLocalFonts()` 报的
   * `family` 完全一致（理论上一款字体的显示名与其字节内嵌的族名可能不同，
   * 虽然真实浏览器环境下两者几乎总是一致——见下面 onChange 里的用法）。
   */
  const importSystemFont = async (family: string): Promise<FontRecord> => {
    const handle = lastSystemFontQuery.find((f) => f.family === family);
    if (handle === undefined) throw new Error(`没有找到系统字体「${family}」，请重新查询`);
    const blob = await handle.blob();
    const bytes = new Uint8Array(await blob.arrayBuffer());
    const record: FontRecord = { ...parseFontMetadata(bytes), source: 'system', bytes };
    fonts.register(record);
    setFontVersion((v) => v + 1);
    return record;
  };

  const handleImportSystemFont = async (family: string): Promise<void> => {
    await importSystemFont(family);
  };

  const handleUploadFont = async (bytes: Uint8Array): Promise<void> => {
    const userFonts = await UserFontStore.open();
    if (userFonts === null) {
      throw new Error('这个浏览器不支持本地字体存储（OPFS 不可用），无法保存上传的字体');
    }
    const record = await userFonts.add(bytes);
    fonts.register(record);
    setFontVersion((v) => v + 1);
  };

  // 字体下拉框直接列出系统字体名字（不需要先打开"管理字体"对话框手动导入才能看到）：
  // 文字工具第一次激活时自动查询一遍系统字体的轻量元数据（只有名字，不取字节），
  // 混进下拉框的候选列表；用户真正选中某一款未加载的名字时才异步取字节注册
  // （见下面 choicesOverride/onChange 里的处理）——查询只做一次，`null` 之外
  // 的任何结果（含空数组）都不再重新触发，避免每次切回文字工具都重新弹一次权限。
  //
  // 用 `toolManager.subscribe` 挂一个命令式监听器，不用 `createEffect`——这一段
  // 还在 `render()` 建立 Solid root 之前执行，这里起的 `createEffect` 没有 owner
  // 收着（跟上面 `activeChannel` 那条注释是同一个坑），只有 `toolManager` 自己的
  // 订阅机制是安全的（本来就不是 Solid 的响应式原语）。
  const [systemFontNames, setSystemFontNames] = createSignal<readonly string[] | null>(null);
  let systemFontQueryStarted = false;
  toolManager.subscribe(() => {
    if (systemFontQueryStarted || toolManager.active.id !== 'text' || !isLocalFontAccessAvailable()) return;
    systemFontQueryStarted = true;
    void handleQuerySystemFonts()
      .then((names) => setSystemFontNames(names))
      .catch((error: unknown) => {
        store.setHint(`系统字体查询失败：${error instanceof Error ? error.message : String(error)}`);
      });
  });

  const handleRemoveFont = (family: string): void => {
    const record = fonts.records.find((r) => r.family === family);
    fonts.unregister(family);
    setFontVersion((v) => v + 1);
    if (record?.source === 'user') {
      void UserFontStore.open().then((userFonts) => userFonts?.remove(record.family, record.style));
    }
  };

  const textController = new TextToolController({
    store,
    tools: toolManager,
    fonts,
    requestRender: () => setVersion((v) => v + 1),
    toScreen: (point) => {
      const viewport = bridge.lastViewport() ?? { scale: 1, rotation: 0, tx: 0, ty: 0 };
      const view = document.querySelector('[data-testid="canvas-view"]');
      const rect = view?.getBoundingClientRect();
      const screen = documentToScreen(viewport, point);
      return { x: (rect?.left ?? 0) + screen.x, y: (rect?.top ?? 0) + screen.y };
    },
    viewportScale: () => bridge.lastViewport()?.scale ?? 1,
    fill: textFillValue,
    stroke: textStrokeValue,
    setHiddenLayer: (id) => {
      bridge.setHiddenLayer(id);
    },
  });

  // 竖排切换（M3-16）：菜单动作而非工具属性——横竖排是文本图层的结构属性，
  // 与「图层→投影/描边」（M3-14）同一形态：先接通开关，参数面板是后续任务
  registry.register({
    id: 'text.toggleWritingMode',
    title: '竖排文字',
    group: '图层',
    section: 4,
    order: 0,
    enabled: (s) => {
      if (!s.hasDocument) return false;
      const id = store.activeLayerId;
      const doc = store.document;
      if (id === null || doc === null) return false;
      return findLayer(doc.root, id)?.layer.kind === 'text';
    },
    run: () => {
      const doc = store.document;
      const id = store.activeLayerId;
      if (doc === null || id === null) return;
      const found = findLayer(doc.root, id);
      if (found === null || found.layer.kind !== 'text') return;
      const layer = found.layer;
      const nextMode = layer.writingMode === 'vertical' ? 'horizontal' : 'vertical';
      const specRuns = specRunsFromTextRuns(layer.runs);
      if (specRuns === null) {
        store.setHint('字体尚未加载完成');
        return;
      }

      // 结构（writingMode）+ 像素（重新栅格化）合成一条历史（M3-11 引入的通道）
      const transaction = store.beginPixelTransaction('切换排版方向');
      const result = rasterizeText(
        store.tiles,
        id,
        {
          runs: specRuns,
          lineHeight: layer.runs[0]?.lineHeight ?? 1.5,
          boxWidth: layer.box.width,
          writingMode: nextMode,
        },
        { x: layer.box.x, y: layer.box.y },
        transaction,
      );
      store.dispatchWithPixels(createSetWritingModeCommand(id, nextMode), transaction.end());
      store.invalidatePixels([...result.dirty]);
    },
  });

  // 文字变形（对比 Photoshop 补全第一档缺口 阶段 5）：跟竖排切换同一形态
  // （结构+像素合成一条历史），区别是需要先弹一个对话框问样式/弯曲度——
  // 只对横排生效，竖排文字变形不在这次范围内
  const [warpDialogState, setWarpDialogState] = createSignal<WarpTextDialogState | null>(null);
  const applyTextWarp = (state: WarpTextDialogState): void => {
    setWarpDialogState(null);
    const doc = store.document;
    const id = store.activeLayerId;
    if (doc === null || id === null) return;
    const found = findLayer(doc.root, id);
    if (found === null || found.layer.kind !== 'text') return;
    const layer = found.layer;
    const specRuns = specRunsFromTextRuns(layer.runs);
    if (specRuns === null) {
      store.setHint('字体尚未加载完成');
      return;
    }
    const warp = state.style === 'none' ? undefined : { style: state.style, bend: state.bend };
    const transaction = store.beginPixelTransaction('文字变形');
    const result = rasterizeText(
      store.tiles,
      id,
      {
        runs: specRuns,
        lineHeight: layer.runs[0]?.lineHeight ?? 1.5,
        boxWidth: layer.box.width,
        writingMode: layer.writingMode,
        ...(warp === undefined ? {} : { warp }),
      },
      { x: layer.box.x, y: layer.box.y },
      transaction,
    );
    const command = warp === undefined ? createRemoveTextWarpCommand(id) : createSetTextWarpCommand(id, warp);
    store.dispatchWithPixels(command, transaction.end());
    store.invalidatePixels([...result.dirty]);
  };

  registry.register({
    id: 'text.warp',
    title: '文字变形…',
    group: '图层',
    section: 4,
    order: 1,
    enabled: (s) => {
      if (!s.hasDocument) return false;
      const id = store.activeLayerId;
      const doc = store.document;
      if (id === null || doc === null) return false;
      const layer = findLayer(doc.root, id)?.layer;
      return layer?.kind === 'text' && layer.writingMode === 'horizontal';
    },
    run: () => {
      const doc = store.document;
      const id = store.activeLayerId;
      if (doc === null || id === null) return;
      const layer = findLayer(doc.root, id)?.layer;
      if (layer?.kind !== 'text') return;
      setWarpDialogState(
        layer.warp === undefined ? { style: 'none', bend: 0 } : { style: layer.warp.style, bend: layer.warp.bend },
      );
    },
  });

  const brushController = new BrushToolController({
    store,
    tools: toolManager,
    requestRender: () => setVersion((v) => v + 1),
    color: () => store.foreground,
  });
  const transformController = new TransformToolController({
    store,
    requestRender: () => setVersion((v) => v + 1),
    setHiddenLayer: (id) => {
      bridge.setHiddenLayer(id);
    },
  });
  const placeController = new PlaceToolController({
    store,
    tiles: store.tiles,
    nextLayerId: () => `layer-${Date.now().toString(36)}-${++layerSeq}`,
    setActiveLayer: (id) => store.setActiveLayer(id),
    requestRender: () => setVersion((v) => v + 1),
    snapEnabled,
    viewportScale: () => bridge.lastViewport()?.scale ?? 1,
  });
  // 液化（ADR-0013）：不经 ToolManager 挂进常驻工具栏，是"滤镜→液化"打开的
  // 独立会话，同真实 Photoshop 液化对话框自带一套工具
  const liquifyController = new LiquifyController({
    store,
    requestRender: () => setVersion((v) => v + 1),
  });
  const [liquifyOpen, setLiquifyOpen] = createSignal(false);
  registry.register({
    id: 'filter.liquify',
    title: '液化…',
    group: '滤镜',
    section: 0,
    order: 100, // 排在 registerFilterActions 注册的几个滤镜之后
    enabled: (s) => s.hasActiveLayer,
    run: () => {
      if (!liquifyController.begin()) {
        store.setHint('液化需要一个有内容的活动图层');
        return;
      }
      setLiquifyOpen(true);
    },
  });
  // 操控变形（Puppet Warp 计划）：同液化，不经 ToolManager，是独立会话
  const puppetWarpController = new PuppetWarpController({
    store,
    requestRender: () => setVersion((v) => v + 1),
  });
  const [puppetWarpOpen, setPuppetWarpOpen] = createSignal(false);
  registry.register({
    id: 'edit.puppetWarp',
    title: '操控变形…',
    group: '编辑',
    section: 1,
    order: 11, // 「变换」子菜单占了 0-10，这个不进子菜单，排在它们之后
    enabled: (s) => s.hasActiveLayer,
    run: () => {
      if (!puppetWarpController.begin()) {
        store.setHint('操控变形需要一个有内容的活动图层');
        return;
      }
      setPuppetWarpOpen(true);
    },
  });
  // 拖放置入是个临时会话，进入/结束时把工具切到隐藏的 'place'/切回来，
  // 这样属性栏（读 activeTool()）才会显示"宽/高/等比"那三栏——跟裁剪
  // 工具借工具切换驱动重绘是同一个理由，见下面 cropController 的注释。
  let toolBeforePlace: string | null = null;
  const beginPlace = (fileName: string, image: DecodedImage, docWidth: number, docHeight: number): void => {
    toolBeforePlace = toolManager.active.id;
    placeController.begin(fileName, image, docWidth, docHeight);
    toolManager.setActive('place');
  };
  const endPlace = (): void => {
    if (toolBeforePlace !== null) {
      toolManager.setActive(toolBeforePlace);
      toolBeforePlace = null;
    }
  };
  const moveController = new MoveToolController({
    store,
    tools: toolManager,
    requestRender: () => setVersion((v) => v + 1),
    setHiddenLayer: (id) => {
      bridge.setHiddenLayer(id);
    },
    snapEnabled,
    viewportScale: () => bridge.lastViewport()?.scale ?? 1,
  });
  const cropController = new CropToolController({
    store,
    tools: toolManager,
    requestRender: () => setVersion((v) => v + 1),
  });
  // 切到别的工具等价于按 Enter：确定裁剪并让选框消失（PS 的手感——裁剪框
  // 不会因为切了工具就悬在那里没有着落）；Esc 取消是唯一"不提交就消失"的路。
  //
  // 顺带修一个连带的问题：切工具只会冒 toolVersion（驱动菜单/工具栏这些
  // UI 控件重渲染），画布本身的重绘只认 renderVersion（即这里的 version()）
  // ——点了裁剪工具但没碰画布之前，doc-overlay 压根没有机会再跑一次，
  // 裁剪框（以及它的默认满画布框）就得等下一次真正的指针事件才画得出来。
  // 工具切换本身也该算一次"画面需要重绘"。
  let lastToolIdForCrop = toolManager.active.id;
  toolManager.subscribe(() => {
    const activeId = toolManager.active.id;
    if (lastToolIdForCrop === 'crop' && activeId !== 'crop' && cropController.active) {
      cropController.commit();
    }
    lastToolIdForCrop = activeId;
    setVersion((v) => v + 1);
  });
  const perspectiveCropController = new PerspectiveCropToolController({
    store,
    tools: toolManager,
    requestRender: () => setVersion((v) => v + 1),
  });
  const vanishingPointController = new VanishingPointToolController({
    store,
    tools: toolManager,
    requestRender: () => setVersion((v) => v + 1),
    color: () => store.foreground,
  });
  const stampController = new StampToolController({
    store,
    tools: toolManager,
    requestRender: () => setVersion((v) => v + 1),
  });
  const redEyeController = new RedEyeToolController({
    store,
    tools: toolManager,
    requestRender: () => setVersion((v) => v + 1),
  });
  const patchController = new PatchToolController({
    store,
    tools: toolManager,
    requestRender: () => setVersion((v) => v + 1),
  });
  const penController = new PenToolController({
    store,
    tools: toolManager,
    requestRender: () => setVersion((v) => v + 1),
    fill: shapeFillValue,
    stroke: shapeStrokeValue,
  });
  const quickSelectController = new QuickSelectToolController({
    store,
    tools: toolManager,
    requestRender: () => setVersion((v) => v + 1),
  });
  const magicWandController = new MagicWandToolController({
    store,
    tools: toolManager,
    requestRender: () => setVersion((v) => v + 1),
  });
  const magneticLassoController = new MagneticLassoToolController({
    store,
    tools: toolManager,
    requestRender: () => setVersion((v) => v + 1),
  });
  // 自定义渐变色标：null = 用默认的前景→背景（向后兼容，也是没打开过
  // 渐变编辑器时的样子）。不走 ToolManager——色标数组不是 ToolOptionValue
  // 能装的类型（见 gradient-tool.ts 文件头的注释）。
  const [gradientStops, setGradientStops] = createSignal<readonly GradientStopInput[] | null>(null);
  const [gradientEditorOpen, setGradientEditorOpen] = createSignal(false);
  /** 自定义色标 ?? 默认前景→背景——预览按钮、编辑器打开时都读这个。 */
  const effectiveGradientStops = (): readonly GradientStopInput[] =>
    gradientStops() ?? [
      { offset: 0, color: store.foreground, alpha: 1 },
      { offset: 1, color: store.background, alpha: 1 },
    ];
  const gradientController = new GradientToolController({
    store,
    tools: toolManager,
    requestRender: () => setVersion((v) => v + 1),
    stops: () => gradientStops(),
  });
  const paintBucketController = new PaintBucketToolController({
    store,
    tools: toolManager,
    requestRender: () => setVersion((v) => v + 1),
  });
  const healController = new HealToolController({
    store,
    tools: toolManager,
    requestRender: () => setVersion((v) => v + 1),
  });
  const shapeController = new ShapeToolController({
    store,
    tools: toolManager,
    requestRender: () => setVersion((v) => v + 1),
    fill: shapeFillValue,
    stroke: shapeStrokeValue,
  });
  bridge.setDocOverlay((ctx) => {
    brushController.drawPreview(ctx);
    healController.drawPreview(ctx);
    transformController.drawOverlay(ctx, bridge.lastViewport()?.scale ?? 1);
    textController.drawOverlay(ctx, bridge.lastViewport()?.scale ?? 1);
    placeController.drawOverlay(ctx, bridge.lastViewport()?.scale ?? 1);
    moveController.drawOverlay(ctx, bridge.lastViewport()?.scale ?? 1);
    cropController.drawOverlay(ctx, bridge.lastViewport()?.scale ?? 1);
    perspectiveCropController.drawOverlay(ctx);
    vanishingPointController.drawOverlay(ctx);
    penController.drawOverlay(ctx);
    gradientController.drawOverlay(ctx);
    shapeController.drawOverlay(ctx);
    patchController.drawOverlay(ctx);
    stampController.drawOverlay(ctx);
    drawMarqueeOverlay(ctx);
    drawLassoOverlay(ctx);
    drawSelectedPathOverlay(ctx);
  });

  /**
   * 黑白交替虚线描边——跟提交后蚂蚁线（ants-overlay.ts 的着色器）同一套
   * 视觉语言。单色白色虚线在白色背景的图片上会完全隐形（真实反馈：Safari
   * 上打开一张白底图拖矩形选框，边框"看不清"）；黑白间隔保证无论背景是
   * 什么颜色，虚线里总有一半线段能跟它对比出来。用两遍 stroke 实现：
   * 同一条 path，第二遍把 dash 相位错开半个周期，黑白刚好落在对方的间隙里。
   */
  function strokeMarchingOutline(ctx: CanvasRenderingContext2D, drawPath: () => void): void {
    ctx.save();
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 4]);
    ctx.strokeStyle = 'black';
    ctx.lineDashOffset = 0;
    drawPath();
    ctx.stroke();
    ctx.strokeStyle = 'white';
    ctx.lineDashOffset = 4;
    drawPath();
    ctx.stroke();
    ctx.restore();
  }

  /**
   * 选框拖拽期间的实时预览——矩形/椭圆虚线轮廓，松手前就要看得见选区
   * 大致范围，不是等提交后蚂蚁线才出现。marquee.ts 的手势类刻意保持
   * "纯计算，无 DOM"（文件头注释），画布绘制留在装配层做。
   */
  function drawMarqueeOverlay(ctx: CanvasRenderingContext2D): void {
    if (!rectGesture.active) return;
    const style = toolManager.getOption('marquee', 'style') as MarqueeStyle;
    const rect = currentMarqueeRect();
    const minSize = style === 'fixedSize' ? 0 : 1;
    if (rect === null || rect.width < minSize || rect.height < minSize) return;
    const shape = toolManager.getOption('marquee', 'shape') as 'rectangle' | 'ellipse';
    const radius = toolManager.getOption('marquee', 'radius') as number;
    strokeMarchingOutline(ctx, () => {
      ctx.beginPath();
      if (shape === 'ellipse') {
        ctx.ellipse(
          rect.x + rect.width / 2,
          rect.y + rect.height / 2,
          rect.width / 2,
          rect.height / 2,
          0,
          0,
          Math.PI * 2,
        );
      } else if (radius > 0) {
        // 跟 fromRoundedRect 同一个夹取规则：半径不能超过短边一半，
        // 预览和最终提交的选区形状必须一致，不能预览一个样、落地又变样
        const r = Math.min(radius, rect.width / 2, rect.height / 2);
        ctx.roundRect(rect.x + 0.5, rect.y + 0.5, rect.width - 1, rect.height - 1, r);
      } else {
        ctx.rect(rect.x + 0.5, rect.y + 0.5, rect.width - 1, rect.height - 1);
      }
    });
  }

  /**
   * 套索拖拽期间的实时预览：自由套索画已走过的轨迹（不闭合，闭合是松手
   * 之后蚂蚁线的事）；多边形套索画已落下的顶点连线 + 到当前指针位置的
   * 橡皮筋预览线（下一次点击会落在哪条边上，落笔前就看得见）。
   */
  function drawLassoOverlay(ctx: CanvasRenderingContext2D): void {
    const strokePath = (points: readonly { x: number; y: number }[], close: boolean): void => {
      if (points.length < 2) return;
      strokeMarchingOutline(ctx, () => {
        ctx.beginPath();
        ctx.moveTo(points[0]!.x, points[0]!.y);
        for (let i = 1; i < points.length; i++) ctx.lineTo(points[i]!.x, points[i]!.y);
        if (close) ctx.closePath();
      });
    };

    if (freehand.active) {
      strokePath(freehand.points(), false);
      return;
    }
    if (polygon.active) {
      const points = polygon.points();
      const full = polygonCursor === null ? points : [...points, polygonCursor];
      strokePath(full, false);
      ctx.save();
      // 顶点点也要在白底图上看得清：黑色描边 + 白色填充，同一套对比逻辑
      ctx.fillStyle = 'white';
      ctx.strokeStyle = 'black';
      ctx.lineWidth = 1;
      for (const p of points) {
        ctx.beginPath();
        ctx.arc(p.x, p.y, 3, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
      }
      ctx.restore();
    }
    if (magneticLassoController.active) {
      strokePath(magneticLassoController.previewPath(), false);
      ctx.save();
      // 紧固点用方块跟多边形套索的圆点标记区分——真实 Photoshop 磁性
      // 套索的紧固点视觉上也是方块，这里保持同一套黑白对比描边手法
      ctx.fillStyle = 'white';
      ctx.strokeStyle = 'black';
      ctx.lineWidth = 1;
      for (const p of magneticLassoController.anchorPoints()) {
        ctx.beginPath();
        ctx.rect(p.x - 3, p.y - 3, 6, 6);
        ctx.fill();
        ctx.stroke();
      }
      ctx.restore();
    }
  }

  const onToolPointer = (
    phase: 'down' | 'move' | 'up' | 'double',
    point: { x: number; y: number },
    modifiers: { shiftKey: boolean; altKey: boolean },
    samples: readonly ToolSample[],
  ): void => {
    const doc = store.document;
    if (doc === null) return;
    // 色彩范围对话框开着时，点击画布永远是"取样参考色"，优先于任何工具
    // 分发（同变换会话/置入会话的"绝对优先"惯例）——不然点一下画布，
    // 用户当前选中的是什么工具（画笔？橡皮擦？）就会先执行那个工具的
    // 动作，取样反而不生效
    if (colorRangeOpen() && phase === 'down') {
      const sampled = sampleDocumentColor(store, point.x, point.y);
      if (sampled !== null) {
        colorRangeRefRgb = sampled;
        setColorRangeSwatch(`#${sampled.r.toString(16).padStart(2, '0')}${sampled.g.toString(16).padStart(2, '0')}${sampled.b.toString(16).padStart(2, '0')}`);
        applyColorRangePreview();
      }
      return;
    }
    // 光标预览独立于工具分发链——不消费事件，纯粹为了让吸管的取色圆环
    // 跟手实时更新，哪怕后面的工具分发把这次事件吞掉了也不影响
    if (toolManager.active.id === 'eyedropper' && (phase === 'down' || phase === 'move')) {
      setEyedropperColor(sampleDocumentColor(store, point.x, point.y));
    }
    // 变换会话优先接管一切指针（手感容差按视口缩放换算：屏幕 6px）
    const scale = bridge.lastViewport()?.scale ?? 1;
    if (transformController.handlePointer(phase, point, 6 / scale)) return;
    if (placeController.handlePointer(phase, point, 6 / scale)) return;
    if (moveController.handlePointer(phase, point)) return;
    if (textController.handlePointer(phase, point)) return;
    if (brushController.handlePointer(phase, samples)) return;
    if (healController.handlePointer(phase, samples)) return;
    if (stampController.handlePointer(phase, modifiers, samples)) return;
    if (redEyeController.handlePointer(phase, point)) return;
    if (patchController.handlePointer(phase, point)) return;
    if (penController.handlePointer(phase, point)) return;
    if (handleEyedropper(store, toolManager.active.id, phase, point)) return;
    if (magneticLassoController.handlePointer(phase, point, modifiers)) return;
    if (quickSelectController.handlePointer(phase, point, modifiers)) return;
    if (magicWandController.handlePointer(phase, point, modifiers)) return;
    if (gradientController.handlePointer(phase, point)) return;
    if (paintBucketController.handlePointer(phase, point)) return;
    if (shapeController.handlePointer(phase, point)) return;
    if (cropController.handlePointer(phase, point, 6 / scale)) return;
    if (perspectiveCropController.handlePointer(phase, point)) return;
    if (vanishingPointController.handlePointer(phase, point)) return;
    const tool = toolManager.active.id;
    const { width, height } = doc.size;

    if (tool === 'marquee') {
      const shape = toolManager.getOption('marquee', 'shape') as 'rectangle' | 'ellipse';
      if (phase === 'down') {
        gestureCombine = combineFromModifiers(modifiers, toolManager.getOption('marquee', 'combine') as SelectionCombine);
        rectGesture.begin(point);
      } else if (phase === 'move' && rectGesture.active) {
        rectGesture.update(point);
        setVersion((v) => v + 1); // 拖拽期间重绘 doc-overlay，选框实时可见
      } else if (phase === 'up' && rectGesture.active) {
        const style = toolManager.getOption('marquee', 'style') as MarqueeStyle;
        const rect = currentMarqueeRect();
        rectGesture.end(); // 只用它复位内部状态，返回值不用——已经按样式算好了
        const radius = toolManager.getOption('marquee', 'radius') as number;
        // 固定大小：尺寸是用户在属性栏敲定的，跟拖拽距离无关，点一下不拖
        // 也该落一个框，不适用"拖拽 <1px 视为误触"这条兜底
        const minSize = style === 'fixedSize' ? 0 : 1;
        if (rect !== null && rect.width >= minSize && rect.height >= minSize) {
          const base = store.selection;
          const mask = marqueeMask(shape, width, height, rect, radius);
          const result = applyCombine(base, mask, gestureCombine);
          store.setSelection(result);
          lastMarqueeCommit = shape === 'rectangle' ? { shape, rect, combine: gestureCombine, base, result } : null;
        }
      }
      return;
    }

    if (tool === 'lasso') {
      const mode = toolManager.getOption('lasso', 'mode') as 'freehand' | 'polygon';
      const lassoButtonMode = toolManager.getOption('lasso', 'combine') as SelectionCombine;
      if (mode === 'freehand') {
        if (phase === 'down') {
          gestureCombine = combineFromModifiers(modifiers, lassoButtonMode);
          freehand.begin(point);
        } else if (phase === 'move' && freehand.active) {
          freehand.add(point);
          setVersion((v) => v + 1); // 拖拽期间重绘 doc-overlay，套索轨迹实时可见
        } else if (phase === 'up' && freehand.active) {
          const points = freehand.end();
          if (points !== null) commitSelection(lassoMask(width, height, points));
        }
        return;
      }
      // 多边形模式：down 加顶点（碰到起点即闭合），double 强制闭合；
      // move 只用来更新"下一条边"的橡皮筋预览，不落顶点
      if (phase === 'down') {
        if (!polygon.active) gestureCombine = combineFromModifiers(modifiers, lassoButtonMode);
        if (polygon.addVertex(point)) {
          const points = polygon.end();
          if (points !== null) commitSelection(lassoMask(width, height, points));
          polygonCursor = null;
        }
        setVersion((v) => v + 1);
      } else if (phase === 'move' && polygon.active) {
        polygonCursor = point;
        setVersion((v) => v + 1);
      } else if (phase === 'double') {
        const points = polygon.end();
        if (points !== null) commitSelection(lassoMask(width, height, points));
        polygonCursor = null;
      }
    }
  };

  /* ---------------- 蚂蚁线 ---------------- */

  const ants = new AntsOverlay();
  void ants.init().then((available) => {
    if (!available) return;
    bridge.setOverlay((ctx) => {
      if (store.selection !== null) ants.drawTo(ctx);
    });
    // 动画节拍：相位推进 → GPU 渲染+readback → 触发一次重绘。
    // 选区/视口没就绪时空转，开销为零
    window.setInterval(() => {
      const mask = store.selection;
      const viewport = bridge.lastViewport();
      const view = bridge.lastView();
      if (mask === null || viewport === null || view === null) {
        ants.clear();
        return;
      }
      void ants.tick(mask, viewport, view).then((rendered) => {
        if (rendered) setVersion((v) => v + 1);
      });
    }, 150);
  });

  /* ---------------- 全局快捷键 ---------------- */

  window.addEventListener('keydown', (event) => {
    // 文本编辑等输入场景：一切全局快捷键让位给输入框
    // （textarea 自己处理 ⌘+Enter 提交 / Esc 取消）
    if (isEditableTarget(event.target)) return;
    // 变换会话中的 Enter/Esc 优先于一切
    if (transformController.active) {
      if (event.key === 'Enter') {
        event.preventDefault();
        void transformController.commit();
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        transformController.cancel();
        return;
      }
      // Delete/Backspace：跟真实 Photoshop 不一样的产品选择——用户明确要的是
      // "放弃这次变换，连这个图层也不要了"，不是清除选区（变换会话里也没有
      // 选区可清）。
      if (event.key === 'Delete' || event.key === 'Backspace') {
        event.preventDefault();
        const id = transformController.layerId;
        transformController.cancel();
        if (id !== null) deleteLayer(store, id);
        return;
      }
    }
    // 拖放置入会话的 Enter/Esc（同级，两者不会同时活跃——一个要激活的
    // 前提是"顶层有可变换的 raster 图层"，另一个此刻那个图层压根没落进
    // 文档，谁也抢不了谁的会话）
    if (placeController.active) {
      if (event.key === 'Enter') {
        event.preventDefault();
        // 不等 commit() 的 Promise resolve 才切工具：栅格化写盘是它最慢
        // 的一步（大图能到一两秒），placeController.commit() 内部在这步
        // 之前已经同步清空了会话（退出编辑框），这里紧跟着调 endPlace()
        // 能立刻把工具切回去，回车的观感才是"立刻退出"，不是等写盘写完
        void placeController.commit();
        endPlace();
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        placeController.cancel();
        endPlace();
        return;
      }
    }
    // 裁剪会话的 Enter/Esc（与变换同级，但变换优先——两者不会同时活跃）
    if (cropController.active) {
      if (event.key === 'Enter') {
        event.preventDefault();
        cropController.commit();
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        cropController.cancel();
        return;
      }
    }
    // 透视裁剪会话的 Enter/Esc
    if (perspectiveCropController.active) {
      if (event.key === 'Enter') {
        event.preventDefault();
        perspectiveCropController.commit();
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        perspectiveCropController.cancel();
        return;
      }
    }
    // 消失点会话的 Enter/Esc
    if (vanishingPointController.active) {
      if (event.key === 'Enter') {
        event.preventDefault();
        vanishingPointController.commit();
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        vanishingPointController.cancel();
        return;
      }
    }
    // 钢笔：Cmd+Return（Mac）/ Ctrl+Enter（Win）随时把当前路径（不管在画
    // 还是已经提交成工作路径，不管模式是「形状」还是「路径」）转成选区、
    // 隐藏钢笔线——真实 PS 的键位，检查过跟浏览器默认快捷键不冲突（这个
    // 组合键在网页里没有原生含义）。只有真的转换成功才 preventDefault，
    // 没有路径可转时放行，不占坑。
    if (
      (event.ctrlKey || event.metaKey) &&
      !event.shiftKey &&
      !event.altKey &&
      event.key === 'Enter' &&
      penController.convertCurrentToSelectionAndHide()
    ) {
      event.preventDefault();
      return;
    }
    // 磁性套索手势的 Enter（闭合）/ Esc（放弃）——跟真实 Photoshop 一致，
    // 双击也能闭合（onToolPointer 里 'double' 分支处理，两条路并存）
    if (magneticLassoController.active) {
      if (event.key === 'Enter') {
        event.preventDefault();
        magneticLassoController.commit();
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        magneticLassoController.cancel();
        return;
      }
    }
    // 钢笔会话的 Enter（提交开放路径）/ Esc（放弃）
    if (penController.active) {
      if (event.key === 'Enter') {
        event.preventDefault();
        penController.commitOpen();
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        penController.cancel();
        return;
      }
    }
    // Cmd/Ctrl+K：命令面板（M4-10），在动作快捷键匹配之前拦截，
    // 免得注册表将来某天挂了同一个键位的动作
    if ((event.ctrlKey || event.metaKey) && !event.shiftKey && !event.altKey && event.key.toUpperCase() === 'K') {
      event.preventDefault();
      setCommandPaletteOpen(true);
      return;
    }
    // Ctrl/Cmd+C/X/V：剪切/拷贝/粘贴，见 clipboard-actions.ts 文件头——
    // 焦点在可编辑元素（文字工具编辑框、命令面板搜索框……）时放行给浏览器
    // 原生行为，不劫持成"操作画布内容"；只在没有可编辑焦点时才接管
    if (
      (event.ctrlKey || event.metaKey) &&
      !event.shiftKey &&
      !event.altKey &&
      !isEditableTarget(event.target)
    ) {
      const key = event.key.toUpperCase();
      if (key === 'C' || key === 'X' || key === 'V') {
        event.preventDefault();
        registry.execute(key === 'C' ? 'edit.copy' : key === 'X' ? 'edit.cut' : 'edit.paste', services);
        return;
      }
    }
    // 先动作（带修饰键），后工具（单字母）——两边的匹配条件天然互斥。
    // 按有效快捷键（用户覆盖优先，M5-03）匹配，不是 registry.actionForKey
    // 那条只认出厂默认值的路
    const action = actionForKeyWithOverrides(registry.list(), shortcutOverrides.overrides, event);
    if (action !== null) {
      // 命中即拦截：禁用状态也不能把 Ctrl+S 之类漏给浏览器默认行为
      event.preventDefault();
      registry.execute(action.id, services);
      return;
    }
    if (toolManager.handleKey(event, { editingText: isEditableTarget(event.target) })) {
      event.preventDefault();
      return;
    }
    // Backspace 与 Delete 同义（PS 惯例）；快捷键表一个动作只挂一个键，这里补别名
    if (event.key === 'Backspace' && !event.ctrlKey && !event.metaKey && !event.altKey) {
      event.preventDefault();
      registry.execute('edit.clear', services);
      return;
    }
    // X 交换前景/背景色（§7.4 之外的 PS 标配键，不与任何工具键冲突）
    if ((event.key === 'x' || event.key === 'X') && !event.ctrlKey && !event.metaKey && !event.altKey) {
      store.swapColors();
      event.preventDefault();
      return;
    }
    // Alt+Delete/Backspace 快速填前景色——历史遗留快捷键，菜单里已经没有
    // 对应项（「填充…」对话框取代了它），只在这里接住，不进动作注册表
    if (
      (event.key === 'Delete' || event.key === 'Backspace') &&
      event.altKey &&
      !event.ctrlKey &&
      !event.metaKey
    ) {
      fillSelectionWithColor(store, store.foreground);
      event.preventDefault();
      return;
    }
    // [ ] 调笔刷大小、Shift+[ ]（{ }）调硬度——§7.4
    if (isEditableTarget(event.target)) return;
    const tool = toolManager.active.id;
    if (tool !== 'brush' && tool !== 'eraser') return;
    const adjust = (key: string, delta: number): void => {
      const current = toolManager.getOption(tool, key) as number;
      toolManager.setOption(tool, key, current + delta);
      event.preventDefault();
    };
    // 用 shiftKey 区分而不是 '{'/'}' 字面量：合成事件（测试）里 Shift+[ 的
    // key 仍是 '['，真实键盘则是 '{'——两种形态都要接住
    const bracket = event.key === '[' || event.key === '{' ? -1 : event.key === ']' || event.key === '}' ? 1 : 0;
    if (bracket !== 0) {
      if (event.shiftKey) adjust('hardness', bracket * 10);
      else adjust('size', bracket * 5);
    }
  });

  /* ---------------- 挂载 ---------------- */

  if (root instanceof HTMLElement) {
    render(
      () => (
        <>
        <AppShell
          hint={hint()}
          statusDocument={statusDocument()}
          menubar={
            <MenuBar
              model={menuModel()}
              onExecute={(id) => {
                registry.execute(id, services);
              }}
            />
          }
          aboutOpen={aboutOpen()}
          onAboutClose={() => {
            setAboutOpen(false);
          }}
          panelVisibility={panelVisibility()}
          panelSlots={{
            properties: (
              <AdjustmentPanel
                layer={activeAdjustmentLayer()}
                onPreview={(adjustment) => {
                  const layer = activeAdjustmentLayer();
                  if (layer !== null) store.setAdjustmentPreview(layer.id, adjustment);
                }}
                onCommit={(adjustment) => {
                  const layer = activeAdjustmentLayer();
                  if (layer === null) return;
                  store.setAdjustmentPreview(layer.id, null);
                  store.dispatch(createSetAdjustmentCommand(layer.id, adjustment));
                }}
              />
            ),
            actions: (
              <ActionsPanel
                isRecording={isRecording()}
                recordingStepCount={(version(), actionRecorder.stepCount)}
                lastSkipped={lastSkipped()}
                actions={recordedActions()}
                onStartRecording={(name) => {
                  const source = store.commandBus;
                  if (source === null) {
                    store.setHint('没有打开的文档，无法开始录制动作');
                    return;
                  }
                  recordingName = name.trim() === '' ? '新动作' : name;
                  actionRecorder.start(source);
                  setIsRecording(true);
                }}
                onStopRecording={() => {
                  const { action, skipped } = actionRecorder.stop(recordingName);
                  setRecordedActions((list) => [...list, action]);
                  setLastSkipped(skipped);
                  setIsRecording(false);
                }}
                onReplay={(action) => {
                  replayAction((command) => store.dispatch(command), action);
                }}
                onSave={(action) => {
                  const bytes = new TextEncoder().encode(serializeAction(action));
                  void gateway.save({ name: `${action.name}.json`, bytes, mimeType: 'application/json' });
                }}
                onLoad={() => {
                  void (async () => {
                    const opened = await gateway.open(['.json']);
                    const file = opened[0];
                    if (file === undefined) return; // 用户取消
                    const text = new TextDecoder('utf-8').decode(file.bytes);
                    try {
                      const action = deserializeAction(text);
                      setRecordedActions((list) => [...list, action]);
                    } catch (error) {
                      store.setHint(`动作导入失败：${error instanceof Error ? error.message : String(error)}`);
                    }
                  })();
                }}
                onRunBatch={(action) => {
                  setBatchAction(action);
                  setBatchDialogOpen(true);
                }}
                onDelete={(name) => {
                  setRecordedActions((list) => list.filter((a) => a.name !== name));
                }}
              />
            ),
            script: (
              <ScriptPanel
                running={scriptRunning()}
                log={scriptLog()}
                onRun={(code) => {
                  setScriptLog([]);
                  setScriptRunning(true);
                  void scriptSandbox
                    .run(code, (line) => setScriptLog((lines) => [...lines, line]))
                    .then((result) => {
                      setScriptRunning(false);
                      setScriptLog((lines) => [
                        ...lines,
                        result.ok ? '（脚本执行完毕）' : `（脚本出错：${result.error ?? '未知错误'}）`,
                      ]);
                    });
                }}
                onStop={() => {
                  scriptSandbox.stop();
                  setScriptRunning(false);
                  setScriptLog((lines) => [...lines, '（已停止）']);
                }}
              />
            ),
            layerComps: (
              <LayerCompsPanel
                comps={layersDocument()?.layerComps ?? []}
                onCreate={(name) => {
                  const document = store.document;
                  if (document === null) return;
                  const id = `comp-${Date.now().toString(36)}-${++layerCompSeq}`;
                  const comp = snapshotLayerComp(document, id, name.trim() === '' ? '未命名复合' : name);
                  store.dispatch(createAddLayerCompCommand(comp));
                }}
                onApply={(id) => {
                  store.dispatch(createApplyLayerCompCommand(id));
                }}
                onDelete={(id) => {
                  store.dispatch(createRemoveLayerCompCommand(id));
                }}
              />
            ),
            layers: (
              <LayerChannelPathTabs
                layers={
                  <LayersPanel
                    document={layersDocument()}
                    activeLayerId={activeLayerId()}
                    selectedLayerIds={selectedLayerIds()}
                    onSelect={(id) => {
                      store.setActiveLayer(layerId(id));
                    }}
                    onSelectionChange={(ids) => {
                      store.setSelectedLayerIds(ids.map((id) => layerId(id)));
                    }}
                    onToggleVisible={(id, visible) => {
                      store.dispatch(createSetVisibleCommand(layerId(id), visible));
                    }}
                    onSetOpacity={(id, opacity) => {
                      store.dispatch(createSetOpacityCommand(layerId(id), opacity));
                    }}
                    onSetBlendMode={(id, mode) => {
                      store.dispatch(createSetBlendModeCommand(layerId(id), mode));
                    }}
                    thumbnail={thumbnailFor}
                    onReorder={(sourceId, targetId) => {
                      if (sourceId === targetId) return;
                      const doc = store.document;
                      if (doc === null) return;
                      const sourceLoc = findLayer(doc.root, layerId(sourceId));
                      const targetLoc = findLayer(doc.root, layerId(targetId));
                      if (sourceLoc === null || targetLoc === null) return;

                      // 拖到一个组的行上 = 挪进这个组里，放在最上面（数组末位在最上，
                      // 跟"新建图层"落位同一个惯例）；moveLayer 本身会拒绝把组挪进
                      // 自己的子孙里（造环），这里不用重复判断
                      if (targetLoc.layer.kind === 'group') {
                        store.dispatch(
                          createReorderLayerCommand({
                            id: layerId(sourceId),
                            newParentId: targetLoc.layer.id,
                            newIndex: targetLoc.layer.children.length,
                          }),
                        );
                        return;
                      }

                      // 落在普通图层上 = 变成它的兄弟，占据它当前所在父组里的位置——
                      // 父组可能是根组，也可能是某个组内部，不再只看根组的直接子图层
                      const targetParent = targetLoc.ancestors[targetLoc.ancestors.length - 1] ?? null;
                      const siblingIds = (targetParent?.children ?? doc.root.children).map((l) => l.id as string);
                      const si = siblingIds.indexOf(sourceId); // 源不在同一个父组时为 -1
                      const ti = siblingIds.indexOf(targetId);
                      if (ti < 0) return;
                      // moveLayer = 先移除再插入：同一父组内重排时，源占据目标的原
                      // 位置——向上移（si<ti）时目标在移除后左移一位，插入点要 +1
                      // 补回；源来自别的父组时不存在这个位移，不用补偿
                      const remaining = siblingIds.filter((id) => id !== sourceId);
                      const index = remaining.indexOf(targetId) + (si >= 0 && si < ti ? 1 : 0);
                      store.dispatch(
                        createReorderLayerCommand({
                          id: layerId(sourceId),
                          newParentId: targetParent?.id ?? null,
                          newIndex: index,
                        }),
                      );
                    }}
                    onToggleClipped={(id, clipped) => {
                      store.dispatch(createSetClippedCommand(layerId(id), clipped));
                    }}
                    onToggleCollapsed={(id, collapsed) => {
                      store.dispatch(createSetCollapsedCommand(layerId(id), collapsed));
                    }}
                    onRename={(id, name) => {
                      store.dispatch(createRenameLayerCommand(layerId(id), name));
                    }}
                    onDelete={(id) => {
                      if (!deleteLayer(store, layerId(id))) store.setHint('没有选中的图层');
                    }}
                    onDuplicateLayer={(id) => {
                      // 跟右键菜单"复制图层"同一套接法：先把拖拽源设为活动图层，
                      // 再走注册表里已有的 layer.duplicate 动作，不重新实现一遍
                      store.setActiveLayer(layerId(id));
                      registry.execute('layer.duplicate', services);
                    }}
                    onLoadSelectionFromLayer={(id, modifiers) => {
                      loadLayerAlphaSelection(store, layerId(id), selectionLoadModeFromModifiers(modifiers));
                    }}
                    onContextMenu={(id, position) => {
                      setContextMenu({ x: position.x, y: position.y, layerId: id });
                    }}
                    onNewLayer={() => registry.execute('layer.new', services)}
                    onNewGroup={() => registry.execute('layer.newGroup', services)}
                    onAddMask={() => registry.execute('layer.addMask', services)}
                    canAddMask={registry.isEnabled('layer.addMask', store.state())}
                    onDeleteActive={() => {
                      const id = store.activeLayerId;
                      if (id === null || !deleteLayer(store, id)) store.setHint('没有选中的图层');
                    }}
                  />
                }
                channels={
                  <ChannelsPanel
                    activeChannel={activeChannel()}
                    onSelectChannel={(channel) => {
                      const next: ChannelRowId = activeChannel() === channel ? 'rgb' : channel;
                      setActiveChannel(next);
                      bridge.setChannelIsolation(next === 'rgb' ? null : next);
                    }}
                    thumbnail={channelThumbnailFor}
                    onLoadSelectionFromChannel={(channel, modifiers) => {
                      if (channel === 'rgb') return; // RGB 合成整体没有对应的单通道字节偏移
                      const offset = channel === 'r' ? 0 : channel === 'g' ? 1 : 2;
                      loadCompositeChannelSelection(store, offset, selectionLoadModeFromModifiers(modifiers));
                    }}
                  />
                }
                paths={
                  <PathsPanel
                    paths={pathRows()}
                    selectedPathId={selectedPathId()}
                    onSelect={(id) => setSelectedPathId(id)}
                    onRename={(id, name) => {
                      renamePath(store, id, name);
                    }}
                    onDelete={(id) => {
                      deletePath(store, id);
                      if (selectedPathId() === id) setSelectedPathId(null);
                    }}
                    onDuplicate={(id) => {
                      duplicatePath(store, id, nextGeneratedPathId);
                    }}
                    onLoadSelection={(id) => {
                      loadPathSelection(store, id);
                    }}
                    onFill={(id) => {
                      if (!fillPath(store, id)) store.setHint('没有可填充的活动像素图层');
                    }}
                    onStroke={(id) => {
                      const doc = store.document;
                      const found = doc?.paths.find((p) => p.id === id);
                      if (found === undefined) return;
                      strokePathOnLayer(store, {
                        path: found.path,
                        size: toolManager.getOption('brush', 'size') as number,
                        hardness: (toolManager.getOption('brush', 'hardness') as number) / 100,
                        color: store.foreground,
                        opacity: (toolManager.getOption('brush', 'opacity') as number) / 100,
                        nextLayerId: () => layerId(nextGeneratedLayerId()),
                      });
                    }}
                  />
                }
              />
            ),
            history: (
              <HistoryPanel
                entries={historyEntries()}
                cursor={historyCursor()}
                onJumpTo={(index) => {
                  const current = historyCursor();
                  if (index === current) return;
                  if (index < current) {
                    for (let i = current; i > index; i--) store.undo();
                  } else {
                    for (let i = current; i < index; i++) store.redo();
                  }
                }}
                onDelete={(index) => {
                  store.deleteHistoryEntry(index);
                }}
              />
            ),
          }}
          toolbar={
            <Toolbar
              // 'place' 是拖放置入触发的隐藏会话工具，不该在工具栏露出按钮——
              // buildSlots() 对不属于任何分组的工具一律画成独立按钮，
              // 这里的过滤是它唯一的"不显示"开关
              tools={toolManager.tools.filter((tool) => tool.id !== 'place')}
              groups={TOOL_GROUPS}
              activeId={activeTool().id}
              onSelect={(id) => {
                toolManager.setActive(id);
              }}
              getOption={(toolId, key) => {
                toolVersion();
                return toolManager.getOption(toolId, key);
              }}
              onSelectMember={(member: ToolGroupMember) => {
                toolManager.setActive(member.toolId);
                if (member.optionOverrides !== undefined) {
                  for (const [key, value] of Object.entries(member.optionOverrides)) {
                    toolManager.setOption(member.toolId, key, value);
                  }
                }
              }}
              foreground={colorCss(foregroundColor())}
              background={colorCss(backgroundColor())}
              onSwapColors={() => {
                store.swapColors();
              }}
              onEditForeground={() => setFgBgEditTarget('foreground')}
              onEditBackground={() => setFgBgEditTarget('background')}
            />
          }
          optionsbar={
            <>
              <Show when={activeTool().id === 'gradient'}>
                <button
                  type="button"
                  class={gradientSwatchStyles.swatch}
                  aria-label="渐变颜色预览与选择"
                  data-testid="gradient-swatch"
                  style={{
                    'background-image': (version(), gradientPreviewBackgroundImage(effectiveGradientStops())),
                  }}
                  onClick={() => {
                    setGradientEditorOpen(true);
                  }}
                />
              </Show>
              {/* 钢笔工具在「形状」模式下跟形状工具共用同一份填充/描边取色状态
                  ——真实 PS 里切换这两个工具，属性栏的填充/描边不会跟着变 */}
              <Show
                when={
                  activeTool().id === 'shape' ||
                  (activeTool().id === 'pen' && (toolVersion(), toolManager.getOption('pen', 'mode') === 'shape'))
                }
              >
                <label class={toolbarStyles.option}>
                  <span class={toolbarStyles.optionLabel}>填充</span>
                  <button
                    type="button"
                    class={gradientSwatchStyles.swatch}
                    aria-label="填充颜色"
                    data-testid="shape-fill-swatch"
                    style={
                      (toolVersion(), toolManager.getOption('shape', 'fillGradient') === true)
                        ? { 'background-image': gradientPreviewBackgroundImage(shapeFillStops()) }
                        : { 'background-image': solidSwatchBackgroundImage(shapeFillColor(), shapeFillAlpha()) }
                    }
                    onClick={() => setColorEditTarget('shapeFill')}
                  />
                </label>
                {/* 直线工具自己的「粗细」就是线宽，不需要再叠一层独立的描边——
                    跟属性栏里 lineWeight/strokeWidth 互斥展示的道理一样；
                    这条规则只对形状工具本身生效，钢笔没有这个"直线"变体 */}
                <Show
                  when={
                    activeTool().id !== 'shape' || (toolVersion(), toolManager.getOption('shape', 'kind') !== 'line')
                  }
                >
                  <label class={toolbarStyles.option}>
                    <span class={toolbarStyles.optionLabel}>描边</span>
                    <button
                      type="button"
                      class={gradientSwatchStyles.swatch}
                      aria-label="描边颜色"
                      data-testid="shape-stroke-swatch"
                      style={
                        (toolVersion(), toolManager.getOption('shape', 'strokeGradient') === true)
                          ? { 'background-image': gradientPreviewBackgroundImage(shapeStrokeStops()) }
                          : { 'background-image': solidSwatchBackgroundImage(shapeStrokeColor(), shapeStrokeAlpha()) }
                      }
                      onClick={() => setColorEditTarget('shapeStroke')}
                    />
                  </label>
                </Show>
              </Show>
              <Show when={activeTool().id === 'text'}>
                <label class={toolbarStyles.option}>
                  <span class={toolbarStyles.optionLabel}>颜色</span>
                  <button
                    type="button"
                    class={gradientSwatchStyles.swatch}
                    aria-label="文字颜色"
                    data-testid="text-fill-swatch"
                    style={
                      (toolVersion(), toolManager.getOption('text', 'fillGradient') === true)
                        ? { 'background-image': gradientPreviewBackgroundImage(textFillStops()) }
                        : { 'background-image': solidSwatchBackgroundImage(textFillColor(), textFillAlpha()) }
                    }
                    onClick={() => setColorEditTarget('textFill')}
                  />
                </label>
                <label class={toolbarStyles.option}>
                  <span class={toolbarStyles.optionLabel}>描边</span>
                  <button
                    type="button"
                    class={gradientSwatchStyles.swatch}
                    aria-label="文字描边颜色"
                    data-testid="text-stroke-swatch"
                    style={
                      (toolVersion(), toolManager.getOption('text', 'strokeGradient') === true)
                        ? { 'background-image': gradientPreviewBackgroundImage(textStrokeStops()) }
                        : { 'background-image': solidSwatchBackgroundImage(textStrokeColor(), textStrokeAlpha()) }
                    }
                    onClick={() => setColorEditTarget('textStroke')}
                  />
                </label>
                <button
                  type="button"
                  data-testid="open-font-manager"
                  onClick={() => setFontManagerOpen(true)}
                >
                  管理字体…
                </button>
              </Show>
              {/* 画笔/消失点这两个工具画出来的颜色其实是全局前景色（跟真实 PS
                  一致，不是每个工具单独维护一份），属性栏本身的选项里
                  （大小/硬度/不透明度…）看不出这一点——不熟悉的人会以为
                  颜色没地方设。这里露出一个只读+可点的前景色快捷入口，
                  点它跟点工具箱那个前景色块弹出的是同一个取色器 */}
              <Show when={activeTool().id === 'brush' || activeTool().id === 'vanishingPoint'}>
                <label class={toolbarStyles.option}>
                  <span class={toolbarStyles.optionLabel}>颜色</span>
                  <button
                    type="button"
                    class={gradientSwatchStyles.swatch}
                    aria-label="画笔颜色（前景色）"
                    data-testid="paint-color-swatch"
                    style={{ background: (version(), colorCss(foregroundColor())) }}
                    onClick={() => setFgBgEditTarget('foreground')}
                  />
                </label>
              </Show>
              {/* 钢笔「路径」模式提交出的工作路径不上色、不进文档，得靠这三个
                  按钮显式转换——对应真实 PS 的 Ctrl+Enter「建立选区」、
                  点属性栏「形状」图标、Paths 面板"存储工作路径" */}
              <Show
                when={
                  activeTool().id === 'pen' &&
                  (toolVersion(), toolManager.getOption('pen', 'mode') === 'path') &&
                  (version(), penController.workPath !== null)
                }
              >
                <button
                  type="button"
                  data-testid="pen-convert-to-selection"
                  onClick={() => penController.convertToSelection()}
                >
                  建立选区
                </button>
                <button
                  type="button"
                  data-testid="pen-convert-to-shape"
                  onClick={() => penController.convertToShape()}
                >
                  建立形状
                </button>
                <button
                  type="button"
                  data-testid="pen-convert-to-path"
                  onClick={() => penController.convertToSavedPath()}
                >
                  存储路径
                </button>
              </Show>
              <OptionsBar
                tool={(() => {
                  toolVersion();
                  if ((version(), transformController.active)) return TRANSFORM_TOOL_DISPLAY;
                  if (activeTool().id === 'shape') {
                    return shapeToolOptionsForKind(activeTool(), toolManager.getOption('shape', 'kind') as string);
                  }
                  if (activeTool().id === 'marquee') {
                    return marqueeToolOptionsForShape(
                      activeTool(),
                      toolManager.getOption('marquee', 'shape') as string,
                      toolManager.getOption('marquee', 'style') as string,
                    );
                  }
                  if (activeTool().id === 'lasso') {
                    return lassoToolOptionsForMode(activeTool(), toolManager.getOption('lasso', 'mode') as string);
                  }
                  if (activeTool().id === 'pen' && toolManager.getOption('pen', 'mode') === 'shape') {
                    const shapeTool = toolManager.tools.find((t) => t.id === 'shape');
                    if (shapeTool !== undefined) return penToolOptionsWithShape(activeTool(), shapeTool);
                  }
                  return activeTool();
                })()}
              value={(key) => {
                toolVersion();
                // 自由变换会话覆盖属性栏——不管当前选的是哪个工具，Ctrl+T
                // 随时能触发，宽/高读的是拖手柄实时改出来的尺寸
                if (transformController.active) {
                  version();
                  if (key === 'width') return transformController.width;
                  if (key === 'height') return transformController.height;
                  if (key === 'uniform') return transformController.uniform;
                  return 0;
                }
                // 裁剪的宽/高不是 ToolManager 记的静态值——跟真实 PS 一样，
                // 这两栏显示的是当前裁剪框的实时尺寸，拖手柄时也要跟着变
                // （所以还要订阅 version()，那才是画布重绘/框改形的信号）
                if (activeTool().id === 'crop' && (key === 'width' || key === 'height')) {
                  version();
                  const rect = cropController.rect;
                  if (rect === null) return 0;
                  return Math.round(key === 'width' ? rect.width : rect.height);
                }
                // 置入会话同理：宽/高读的是拖手柄实时改出来的尺寸，不是
                // ToolManager 记的静态选项
                if (activeTool().id === 'place' && (key === 'width' || key === 'height')) {
                  version();
                  return key === 'width' ? placeController.width : placeController.height;
                }
                if (activeTool().id === 'place' && key === 'uniform') {
                  return placeController.uniform;
                }
                // 钢笔「形状」模式借用的填充/描边/宽度选项实际存在 'shape' 工具名下
                if (activeTool().id === 'pen' && PEN_SHARED_SHAPE_OPTION_KEYS.includes(key)) {
                  return toolManager.getOption('shape', key);
                }
                return toolManager.getOption(activeTool().id, key);
              }}
              onChange={(key, value) => {
                if (transformController.active) {
                  if (key === 'width' || key === 'height') {
                    transformController.applySizeOption(key, value as number);
                  } else if (key === 'uniform') {
                    transformController.setUniform(value as boolean);
                  }
                  return;
                }
                if (activeTool().id === 'crop' && (key === 'width' || key === 'height')) {
                  cropController.applySizeOption(key, value as number);
                  return;
                }
                if (activeTool().id === 'place') {
                  if (key === 'width' || key === 'height') {
                    placeController.applySizeOption(key, value as number);
                  } else if (key === 'uniform') {
                    placeController.setUniform(value as boolean);
                  }
                  return;
                }
                if (activeTool().id === 'pen' && PEN_SHARED_SHAPE_OPTION_KEYS.includes(key)) {
                  toolManager.setOption('shape', key, value);
                  return;
                }
                if (key === 'fontFamily' && !fonts.records.some((r) => r.family === value)) {
                  // 选中的是下拉框里"还没取字节"的系统字体名字——先异步导入，
                  // 成功注册后才真正切过去；不立刻切，是为了不让文字工具的
                  // commit() 在字节到位前用这个族名去排版（那会直接判定
                  // "字体尚未加载完成"、把用户刚打的字整段丢掉，见 text-tool.ts）
                  const family = value as string;
                  store.setHint(`正在加载系统字体「${family}」…`);
                  void importSystemFont(family)
                    .then((record) => {
                      // 富文本混合格式（M4-38）：文字工具编辑中且选中了一段
                      // 文字时，字体改动只作用于选区，不是整段——这里也要走
                      // 一遍，不能因为这款字体是刚异步导入的就漏了这一步。
                      // ⚠️必须先 apply 再 `toolManager.setOption`：没被
                      // "选区改样式"功能碰过的文字，`applyStyle` 内部拿
                      // `tools.getOption('text','fontFamily')` 当"这段
                      // 文字原来的字体"兜底基准——先把这个选项改成新字体，
                      // 基准和目标就变成同一个字体，整段被误认成"都在改
                      // 成新字体"（M4-40f，跟颜色那边是同一类顺序 bug）
                      if (activeTool().id === 'text') {
                        applyTextStylePatch({ fontFamily: record.family });
                        textController.refocus();
                      }
                      // 用解析出来的真实族名切过去，不是下拉框选项的原始
                      // value——两者理论上可能不同（见 importSystemFont 注释）
                      toolManager.setOption(activeTool().id, key, record.family);
                      store.setHint(`字体「${record.family}」已加载`);
                    })
                    .catch((error: unknown) => {
                      store.setHint(error instanceof Error ? error.message : String(error));
                    });
                  return;
                }
                if (activeTool().id === 'text' && (key === 'fontFamily' || key === 'fontSize')) {
                  // 富文本混合格式（M4-38，用户反馈"拖选一部分文字改字体"）：
                  // 编辑中且 textarea 里有真正的选区（不是光标）时，这次改动
                  // 只作用于选中的那段文字，不是整段——跟下面照常执行的
                  // `toolManager.setOption` 并不冲突：属性栏的默认值照样更新
                  // （下一次新建文本/无选区时用这个默认值），两件事互不影响
                  applyTextStylePatch(
                    key === 'fontFamily' ? { fontFamily: value as string } : { fontSize: value as number },
                  );
                  textController.refocus();
                }
                toolManager.setOption(activeTool().id, key, value);
                // 裁剪的「比例」一变就让选框立刻改形，不用等下一次拖手柄
                if (activeTool().id === 'crop' && key === 'aspect') cropController.applyAspectOption();
                // 矩形选框的「半径」一变，刚提交的那个选区立刻跟着变圆角，
                // 不用重新拖一次——同上面裁剪「比例」一样的手感
                if (activeTool().id === 'marquee' && key === 'radius') applyMarqueeRadiusLive(value as number);
              }}
              choicesOverride={(key) => {
                if (key !== 'fontFamily') return undefined;
                fontVersion();
                // 内置字体还没加载完成时用 registry.ts 的静态兜底，不要让
                // 下拉框先空一下再填充
                if (!fonts.loaded) return undefined;
                const loaded = fonts.families;
                // 系统字体查完名字就直接混进下拉框——不需要先手动点"导入"
                // 才看得到；选中未加载的名字时，上面 onChange 里才真正取字节
                const loadedSet = new Set(loaded);
                const systemUnloaded = (systemFontNames() ?? []).filter((f) => !loadedSet.has(f));
                return [...loaded, ...systemUnloaded].map((family) => ({ value: family, label: family }));
              }}
              />
            </>
          }
        >
          <CanvasView
            documentSize={documentSize()}
            onRender={bridge.onRender}
            renderVersion={version()}
            onToolPointer={onToolPointer}
            cursor={cursorForActiveTool()}
            showRulers={showRulers()}
            zoomToolActive={activeTool().id === 'zoom'}
            handToolActive={activeTool().id === 'hand'}
            zoomDirection={(toolVersion(), (toolManager.getOption('zoom', 'zoomOut') as boolean) ? 'out' : 'in')}
            viewCommandRequest={viewCommandRequest()}
            guides={guides()}
            guidesLocked={guidesLocked()}
            onGuideCreate={(axis, position) => {
              addGuide(store, axis, position, guidesLocked());
            }}
            onGuideMove={(index, position) => {
              moveGuide(store, index, position);
            }}
            onGuideRemove={(index) => {
              removeGuideAt(store, index);
            }}
            // 没有文档时不接管右键——让浏览器原生菜单照常弹出，不该拦截
            // 一个我们自己什么都不会做的空白画布
            onContextMenu={
              hasDocument()
                ? (position) => {
                    setCanvasContextMenu(position);
                  }
                : undefined
            }
          />
          <Show when={!hasDocument()}>
            <EmptyCanvasOverlay
              onNewDocument={() => registry.execute('file.new', services)}
              onOpenFile={() => registry.execute('file.open', services)}
              onNewFromCategory={openNewDocumentWithCategory}
              onOpenBatch={() => {
                setBatchAction(null);
                setBatchDialogOpen(true);
              }}
            />
          </Show>
          <NewGuideDialog
            open={newGuideOpen()}
            documentSize={documentSize()}
            onClose={() => {
              setNewGuideOpen(false);
            }}
            onConfirm={(axis, position) => {
              addGuide(store, axis, position, guidesLocked());
              setNewGuideOpen(false);
            }}
          />
          <LayerStyleDialog
            open={layerStyleTargetId() !== null}
            layerName={(() => {
              const id = layerStyleTargetId();
              const doc = store.document;
              if (id === null || doc === null) return '';
              return findLayer(doc.root, layerId(id))?.layer.name ?? '';
            })()}
            effects={(() => {
              const id = layerStyleTargetId();
              const doc = store.document;
              if (id === null || doc === null) return [];
              return findLayer(doc.root, layerId(id))?.layer.effects ?? [];
            })()}
            onClose={() => {
              setLayerStyleTargetId(null);
            }}
            onConfirm={(effects) => {
              const id = layerStyleTargetId();
              if (id !== null) store.dispatch(createSetEffectsCommand(layerId(id), effects));
              setLayerStyleTargetId(null);
            }}
          />
          <Show when={contextMenu()}>
            {(menu) => {
              // 只在这里读一次 menu()，存成普通值——onExecute 里 onClose() 先跑，
              // 会把 contextMenu 信号置空，`<Show>` 随之判假、这个渲染分支被摘掉，
              // 如果 onExecute 里还去调用 menu()（惰性再读一次），读到的就是已经
              // 过期/被摘掉之后的结果，之前踩过这个坑（这几个动作全部悄悄没反应）
              const menuX = menu().x;
              const menuY = menu().y;
              const rightClickedId = menu().layerId;
              const targetId = layerId(rightClickedId);

              const doc = store.document;
              const found = doc === null ? null : findLayer(doc.root, targetId);
              const kind = found?.layer.kind;
              const selected = store.selectedLayerIds;
              const isMultiSelect = selected.size > 1 && selected.has(targetId);
              const blendSubmenu: ContextMenuItem[] = BLEND_MODE_GROUPS.flatMap((group, groupIndex) =>
                group.options.map((option, optionIndex) => ({
                  id: `context-blend:${option.value}`,
                  title: option.label,
                  separatorBefore: groupIndex > 0 && optionIndex === 0,
                })),
              );
              const items: ContextMenuItem[] = [
                { id: 'context:rasterize', title: '栅格化图层', enabled: kind === 'text' || kind === 'shape' },
                { id: 'context:duplicate', title: '复制图层' },
                { id: 'context:blend', title: '混合模式', submenu: blendSubmenu },
                { id: 'context:style', title: '图层样式…' },
                { id: 'context:flatten', title: '拼合图像', separatorBefore: true },
                ...(isMultiSelect
                  ? [
                      { id: 'context:merge', title: '合并图层' },
                      { id: 'context:group', title: '图层编组' },
                    ]
                  : []),
                { id: 'context:delete', title: '删除图层', separatorBefore: true },
              ];
              return (
                <ContextMenu
                  x={menuX}
                  y={menuY}
                  items={items}
                  onClose={() => {
                    setContextMenu(null);
                  }}
                  onExecute={(id) => {
                    if (id.startsWith('context-blend:')) {
                      store.dispatch(
                        createSetBlendModeCommand(targetId, id.slice('context-blend:'.length) as BlendMode),
                      );
                      return;
                    }
                    switch (id) {
                      case 'context:rasterize':
                        if (!rasterizeLayer(store, targetId)) store.setHint('这个图层不需要栅格化');
                        break;
                      case 'context:duplicate':
                        store.setActiveLayer(targetId);
                        registry.execute('layer.duplicate', services);
                        break;
                      case 'context:style':
                        setLayerStyleTargetId(rightClickedId);
                        break;
                      case 'context:flatten':
                        if (!flattenImage(store, nextGeneratedLayerId)) store.setHint('已经是单一图层');
                        break;
                      case 'context:merge':
                        if (!mergeSelectedLayers(store, [...store.selectedLayerIds], nextGeneratedLayerId)) {
                          store.setHint('所选图层不在同一个组里，无法合并');
                        }
                        break;
                      case 'context:group':
                        store.setActiveLayer(targetId);
                        registry.execute('layer.group', services);
                        break;
                      case 'context:delete':
                        if (!deleteLayer(store, targetId)) store.setHint('没有选中的图层');
                        break;
                    }
                  }}
                />
              );
            }}
          </Show>
          <Show when={canvasContextMenu()}>
            {(menu) => {
              // 同图层右键菜单那条注释：只读一次 menu()，存成普通值——
              // onExecute 里 onClose() 先跑会把信号置空，`<Show>` 判假后
              // 再惰性读 menu() 会读到过期状态
              const menuX = menu().x;
              const menuY = menu().y;
              const state = store.state();
              const items: ContextMenuItem[] = [
                { id: 'context:select-all', title: '全部选择', enabled: registry.isEnabled('select.all', state) },
                { id: 'context:deselect', title: '取消选择', enabled: registry.isEnabled('select.deselect', state) },
                { id: 'context:reselect', title: '重新选择', enabled: registry.isEnabled('select.reselect', state) },
                {
                  id: 'context:invert',
                  title: '选择反向',
                  enabled: registry.isEnabled('select.invert', state),
                  separatorBefore: true,
                },
                {
                  id: 'context:feather',
                  title: '羽化…',
                  enabled: registry.isEnabled('select.modify.feather', state),
                },
                {
                  id: 'context:fill',
                  title: '填充…',
                  enabled: registry.isEnabled('edit.fillDialog', state),
                  separatorBefore: true,
                },
                { id: 'context:stroke', title: '描边…', enabled: registry.isEnabled('edit.stroke', state) },
              ];
              return (
                <ContextMenu
                  x={menuX}
                  y={menuY}
                  items={items}
                  onClose={() => {
                    setCanvasContextMenu(null);
                  }}
                  onExecute={(id) => {
                    switch (id) {
                      case 'context:select-all':
                        registry.execute('select.all', services);
                        break;
                      case 'context:deselect':
                        registry.execute('select.deselect', services);
                        break;
                      case 'context:reselect':
                        registry.execute('select.reselect', services);
                        break;
                      case 'context:invert':
                        registry.execute('select.invert', services);
                        break;
                      case 'context:feather':
                        registry.execute('select.modify.feather', services);
                        break;
                      case 'context:fill':
                        registry.execute('edit.fillDialog', services);
                        break;
                      case 'context:stroke':
                        registry.execute('edit.stroke', services);
                        break;
                    }
                  }}
                />
              );
            }}
          </Show>
          <ExportDialog
            open={exportOpen()}
            summary={exportSummary()}
            documentSize={documentSize()}
            onPreviewRequest={requestExportPreview}
            previewUrl={previewUrl()}
            previewSize={previewSize()}
            previewPending={previewPending()}
            onClose={() => {
              setExportOpen(false);
            }}
            onExport={(request) => {
              const baseName = fileSession.fileName.replace(/\.[^.]+$/, '');
              void exportDocument(
                { store, gateway },
                { ...request, baseName },
              ).then(
                (files) => {
                  setExportSummary(
                    files.map((f) => `${f.name}（${f.width}×${f.height}）`).join('、'),
                  );
                  const rasterizedLayers = files.flatMap((f) => f.rasterizedLayers ?? []);
                  store.setHint(
                    rasterizedLayers.length > 0
                      ? `已导出 ${files.length} 个文件；以下图层已栅格化：${rasterizedLayers.join('、')}`
                      : `已导出 ${files.length} 个文件`,
                  );
                },
                (error: unknown) => {
                  store.setHint(`导出失败：${error instanceof Error ? error.message : String(error)}`);
                },
              );
            }}
          />
          <SeamCarveDialog
            open={seamCarveOpen()}
            currentWidth={documentSize().width}
            currentHeight={documentSize().height}
            busy={seamCarveBusy()}
            onClose={() => {
              setSeamCarveOpen(false);
            }}
            onApply={(w, h) => {
              setSeamCarveBusy(true);
              void seamCarveDocument(store, w, h)
                .then((done) => {
                  if (done) store.setHint(`已缩放到 ${w}×${h}`);
                })
                .catch((error: unknown) => {
                  store.setHint(`缩放失败：${error instanceof Error ? error.message : String(error)}`);
                })
                .finally(() => {
                  setSeamCarveBusy(false);
                  setSeamCarveOpen(false);
                });
            }}
          />
          <NewDocumentDialog
            open={newDocumentOpen()}
            initialCategoryId={newDocumentCategoryId()}
            onClose={() => {
              setNewDocumentOpen(false);
            }}
            onConfirm={(info) => {
              const bg = hexToRgb(info.background);
              createNewDocument(fileDeps, fileSession, {
                width: info.width,
                height: info.height,
                title: info.name,
                background: [bg.r, bg.g, bg.b],
                transparent: info.transparent,
              });
              setNewDocumentOpen(false);
            }}
          />
          <BatchDialog
            open={batchDialogOpen()}
            action={batchAction()}
            availableActions={recordedActions()}
            onClose={() => setBatchDialogOpen(false)}
            onOpenFiles={(accept) => gateway.open(accept)}
            onRun={(files, options, onProgress) =>
              runBatch(
                { tiles: store.tiles, gateway, now: () => new Date().toISOString() },
                files,
                options,
                undefined,
                onProgress,
              )
            }
          />
          <FillDialog
            open={fillDialogOpen()}
            busy={fillBusy()}
            onClose={() => {
              setFillDialogOpen(false);
            }}
            onFill={(request: FillRequest) => {
              if (request.contents === 'contentAware') {
                // 内容识别填充需要选区告诉它"洞"在哪——不像纯色那样有个
                // "填满整个图层"的合理回退，没有选区就没法做，得如实告知
                if (store.selection === null || store.selection.isEmpty) {
                  store.setHint('内容识别填充需要先框选一个区域');
                  return;
                }
                setFillBusy(true);
                void contentAwareFillDocument(store, {
                  blendMode: request.blendMode,
                  opacity: request.opacity,
                })
                  .catch((error: unknown) => {
                    store.setHint(`内容识别填充失败：${error instanceof Error ? error.message : String(error)}`);
                  })
                  .finally(() => {
                    setFillBusy(false);
                    setFillDialogOpen(false);
                  });
                return;
              }
              const color =
                request.contents === 'foreground'
                  ? store.foreground
                  : request.contents === 'background'
                    ? store.background
                    : hexToRgb(request.color ?? '#808080');
              fillSelectionWithColor(store, color, {
                blendMode: request.blendMode,
                opacity: request.opacity,
              });
              setFillDialogOpen(false);
            }}
          />
          <StrokeDialog
            open={strokeDialogOpen()}
            defaultColor={(version(), rgbToHex(store.foreground))}
            onClose={() => {
              setStrokeDialogOpen(false);
            }}
            onStroke={(request: StrokeRequest) => {
              strokeSelection(store, hexToRgb(request.color), {
                width: request.width,
                position: request.position,
                blendMode: request.blendMode,
                opacity: request.opacity,
              });
              setStrokeDialogOpen(false);
            }}
          />
          <LevelsDialog
            open={levelsOpen()}
            previewUrl={levelsPreview()}
            onPreview={(params) => setLevelsPreview(computeAdjustmentPreviewUrl(levelsTransform(params)))}
            onClose={() => setLevelsOpen(false)}
            onApply={(params) => {
              if (!applyLevelsDestructive(store, params)) store.setHint('没有可调整的图层');
              setLevelsOpen(false);
            }}
          />
          <CurvesDialog
            open={curvesOpen()}
            previewUrl={curvesPreview()}
            onPreview={(points) => setCurvesPreview(computeAdjustmentPreviewUrl(curvesTransform(points)))}
            onClose={() => setCurvesOpen(false)}
            onApply={(points) => {
              if (!applyCurvesDestructive(store, points)) store.setHint('没有可调整的图层');
              setCurvesOpen(false);
            }}
          />
          <ExposureDialog
            open={exposureOpen()}
            previewUrl={exposurePreview()}
            onPreview={(exposure) => setExposurePreview(computeAdjustmentPreviewUrl(exposureTransform(exposure)))}
            onClose={() => setExposureOpen(false)}
            onApply={(exposure) => {
              if (!applyExposureDestructive(store, exposure)) store.setHint('没有可调整的图层');
              setExposureOpen(false);
            }}
          />
          <HueSaturationDialog
            open={hueSaturationOpen()}
            previewUrl={hueSaturationPreview()}
            onPreview={(params) => setHueSaturationPreview(computeAdjustmentPreviewUrl(hueSaturationTransform(params)))}
            onClose={() => setHueSaturationOpen(false)}
            onApply={(params) => {
              if (!applyHueSaturationDestructive(store, params)) store.setHint('没有可调整的图层');
              setHueSaturationOpen(false);
            }}
          />
          <CanvasSizeDialog
            open={canvasSizeOpen()}
            currentWidth={documentSize().width}
            currentHeight={documentSize().height}
            onClose={() => setCanvasSizeOpen(false)}
            onApply={(width, height, anchor: CanvasAnchor) => {
              if (!resizeCanvas(store, width, height, anchor)) store.setHint('画布大小没有变化');
              setCanvasSizeOpen(false);
            }}
          />
          <ImageSizeDialog
            open={imageSizeOpen()}
            currentWidth={documentSize().width}
            currentHeight={documentSize().height}
            busy={imageSizeBusy()}
            onClose={() => setImageSizeOpen(false)}
            onApply={(width, height) => {
              setImageSizeBusy(true);
              void resizeImage(store, width, height)
                .then((result) => {
                  if (!result.resized) store.setHint('图像大小没有变化');
                  else if (result.skippedVectorLayers > 0) {
                    store.setHint(
                      `已缩放到 ${width}×${height}；${result.skippedVectorLayers} 个形状/文字图层未跟着缩放（已知限制）`,
                    );
                  } else {
                    store.setHint(`已缩放到 ${width}×${height}`);
                  }
                })
                .catch((error: unknown) => {
                  store.setHint(`图像大小调整失败：${error instanceof Error ? error.message : String(error)}`);
                })
                .finally(() => {
                  setImageSizeBusy(false);
                  setImageSizeOpen(false);
                });
            }}
          />
          <SelectionModifyDialog
            kind={modifyDialogKind()}
            onClose={() => {
              setModifyDialogKind(null);
            }}
            onConfirm={(kind, amount) => {
              if (!applySelectionModify(store, kind, amount)) store.setHint('没有选区');
              setModifyDialogKind(null);
            }}
          />
          <ColorRangeDialog
            open={colorRangeOpen()}
            sampledColor={colorRangeSwatch()}
            onClose={() => {
              // 取消：退回打开对话框那一刻的选区，不留下这次调整过程中的
              // 任何实时预览痕迹。顺带让所有还没算完的实时预览请求作废——
              // 不然某次还没算完的旧计算这时候才落定（异步操作，谁先谁后
              // 不由这里决定），会把刚复原的选区又盖掉
              colorRangeGeneration++;
              store.setSelection(colorRangePreviousSelection);
              setColorRangeOpen(false);
            }}
            onConfirm={() => {
              // 确定：选区已经是实时预览的最终状态，这里只负责关掉对话框
              setColorRangeOpen(false);
            }}
            onChange={({ fuzziness, invert: doInvert }) => {
              colorRangeFuzziness = fuzziness;
              colorRangeInvertFlag = doInvert;
              applyColorRangePreview();
            }}
          />
          <WarpTextDialog
            initial={warpDialogState()}
            onClose={() => {
              setWarpDialogState(null);
            }}
            onConfirm={applyTextWarp}
          />
          <FilterDialog
            kind={filterDialogKind()}
            onClose={() => {
              setFilterDialogKind(null);
            }}
            onConfirm={(kind, params) => {
              setFilterDialogKind(null);
              void runFilterWithParams(store, kind, params).then((done) => {
                if (done) store.setHint(`已应用${FILTER_NAMES[kind]}`);
              });
            }}
          />
          <LiquifyDialog
            open={liquifyOpen()}
            canvasWidth={documentSize().width}
            canvasHeight={documentSize().height}
            onClose={() => {
              liquifyController.cancel();
              setLiquifyOpen(false);
            }}
            onPointer={(phase, x, y, settings) => liquifyController.handlePointer(phase, { x, y }, settings)}
            onRequestInitialFrame={() => {
              const size = store.document?.size;
              if (size === undefined) return Promise.resolve(null);
              return liquifyController.previewFrame({ x: 0, y: 0, width: size.width, height: size.height });
            }}
            onApply={async () => {
              const done = await liquifyController.commit();
              setLiquifyOpen(false);
              if (done) store.setHint('已应用液化');
              return done;
            }}
          />
          <PuppetWarpDialog
            open={puppetWarpOpen()}
            onClose={() => {
              puppetWarpController.cancel();
              setPuppetWarpOpen(false);
            }}
            onAddPin={(x, y) => puppetWarpController.addPin({ x, y })}
            onHitTestPin={(x, y, tolerance) => puppetWarpController.hitTestPin({ x, y }, tolerance)}
            onMovePin={(id, x, y) => puppetWarpController.movePin(id, { x, y })}
            onRemovePin={(id) => puppetWarpController.removePin(id)}
            onGetPins={() => puppetWarpController.pins}
            onRequestPreview={() => puppetWarpController.previewFrame(PROXY_LONG_EDGE)}
            onApply={async () => {
              const done = await puppetWarpController.commit();
              setPuppetWarpOpen(false);
              if (done) store.setHint('已应用操控变形');
              return done;
            }}
          />
          <GradientEditorDialog
            open={gradientEditorOpen()}
            stops={effectiveGradientStops()}
            foregroundHex={(version(), rgbToHex(store.foreground))}
            backgroundHex={(version(), rgbToHex(store.background))}
            onClose={() => {
              setGradientEditorOpen(false);
            }}
            onApply={(stops) => {
              setGradientStops(stops);
            }}
          />
          <ColorPickerDialog
            open={colorEditTarget() !== null && !(toolVersion(), colorEditTargetIsGradient())}
            color={colorEditTargetSolid()}
            alpha={colorEditTargetSolidAlpha()}
            onClose={() => setColorEditTarget(null)}
            onConfirm={(hex, alpha) => {
              setColorEditTargetSolid(hex, alpha);
              setColorEditTarget(null);
            }}
          />
          <ColorPickerDialog
            open={fgBgEditTarget() !== null}
            color={(version(), rgbToHex(fgBgEditTarget() === 'background' ? store.background : store.foreground))}
            onClose={() => setFgBgEditTarget(null)}
            onConfirm={(hex) => {
              const color = hexToRgb(hex);
              if (fgBgEditTarget() === 'background') store.setBackground(color);
              else store.setForeground(color);
              setFgBgEditTarget(null);
            }}
          />
          <GradientEditorDialog
            open={colorEditTarget() !== null && (toolVersion(), colorEditTargetIsGradient())}
            stops={colorEditTargetStops()}
            foregroundHex={(version(), rgbToHex(store.foreground))}
            backgroundHex={(version(), rgbToHex(store.background))}
            onClose={() => setColorEditTarget(null)}
            onApply={(stops) => {
              setColorEditTargetStops(stops);
              setColorEditTarget(null);
            }}
          />
          <FontManagerDialog
            open={fontManagerOpen()}
            fonts={(fontVersion(), fonts.records)}
            systemFontsAvailable={isLocalFontAccessAvailable()}
            onClose={() => setFontManagerOpen(false)}
            onQuerySystemFonts={handleQuerySystemFonts}
            onImportSystemFont={handleImportSystemFont}
            onUploadFont={handleUploadFont}
            onRemove={handleRemoveFont}
          />
          <CommandPalette
            open={commandPaletteOpen()}
            entries={paletteEntriesFromActions(effectiveActions(), store.state())}
            onExecute={(id) => {
              registry.execute(id, services);
            }}
            onClose={() => {
              setCommandPaletteOpen(false);
            }}
          />
          <ShortcutEditorDialog
            open={shortcutEditorOpen()}
            actions={registry.list()}
            overrides={(version(), shortcutOverrides.overrides)}
            onClose={() => setShortcutEditorOpen(false)}
            onSetShortcut={(id, shortcut) => shortcutOverrides.set(id, shortcut)}
            onResetOne={(id) => shortcutOverrides.resetOne(id)}
            onResetAll={() => shortcutOverrides.resetAll()}
          />
          <HelpPanel
            open={helpPanelOpen()}
            actions={effectiveActions()}
            onClose={() => setHelpPanelOpen(false)}
            onOpenShortcutEditor={() => {
              setHelpPanelOpen(false);
              setShortcutEditorOpen(true);
            }}
            onOpenAbout={() => {
              setHelpPanelOpen(false);
              setAboutOpen(true);
            }}
          />
        </AppShell>
        <OnboardingTour
          open={onboardingOpen()}
          steps={TOUR_STEPS}
          onSkip={dismissOnboarding}
          onFinish={dismissOnboarding}
        />
        {import.meta.env.DEV ? <PerfPanel bridge={bridge} /> : null}
        </>
      ),
      root,
    );
  }

  /* ---------------- 后台探测 ---------------- */

  const features = detectWasmFeatures();
  setCapabilityHint(
    `${describeWasmFeatures(features)} · 隔离 ${isolation.outcome} · 计算池 ${defaultPoolSize()}` +
      (gateway.canOverwrite ? '' : ` · ${reason}`),
  );

  await bootRecovery(fileDeps);
  if (store.recoverable.length > 0) {
    store.setHint(`检测到未正常关闭的文档，可从「文件 → 恢复上次会话」找回`);
  }
}

boot().catch((error: unknown) => {
  // 引导失败不能让页面白屏：这些都是性能路径，编辑器本身仍应可用。
  console.error('[boot] 启动引导异常，按降级路径继续。', error);
  if (root instanceof HTMLElement) {
    render(() => <AppShell hint="启动引导异常，已降级（详见控制台）" />, root);
  }
});
